import {
  applyTimelinePatches,
  enqueueOfflineAction,
  flushOfflineQueue,
  getOfflineMeta,
  readCacheEnvelope,
  removeCache,
  recordTimelinePatch,
  removeTimelinePatchesForActions,
  saveCache,
} from "./offline";
import { toSingleLineAddress } from "./utils/address";
import { readCustomerHubToken } from "./utils/customerHubSession";

const runtimeBase =
  typeof window !== "undefined" && window.__API_BASE__ ? window.__API_BASE__ : null;

function resolveApiBase() {
  const envBase = import.meta.env?.VITE_API_BASE && import.meta.env.VITE_API_BASE.trim();
  if (envBase) return envBase;
  if (runtimeBase) return runtimeBase;

  if (typeof window !== "undefined") {
    const hostname = (window.location?.hostname || "").toLowerCase();
    if ([
      "www.offlico.com", "offlico.com", "find.offlico.com", "business.offlico.com",
      "localpickr.com", "www.localpickr.com", "find.localpickr.com",
    ].includes(hostname)) {
      return "https://scheduler-api-eu.onrender.com";
    }
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return "http://localhost:8000";
    }
  }

  return "http://localhost:8000";
}

const API_BASE = resolveApiBase();

const CACHE_STORAGE_PREFIX = "scheduler.cache.";
const CALENDAR_RANGE_CACHE_STORAGE_PREFIX = "scheduler.calendar.rangeCache";

export const CONFIG = {
  BASE: API_BASE,
};

let authToken = null;
let lastPrimeAt = 0;
const inFlightJsonGets = new Map();
const lastCacheNetworkFetchAt = new Map();
const SAFE_GET_RETRY_DELAY_MS = 250;

if (typeof window !== "undefined") {
  try {
    authToken = window.localStorage?.getItem("scheduler.token") || null;
  } catch {
    authToken = null;
  }
}

export function setAuthToken(token) {
  authToken = token;
}

function emitStockRefresh() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("stock:refresh"));
}

export function primeCoreCaches(force = false) {
  if (!authToken) return;
  const now = Date.now();
  if (!force && now - lastPrimeAt < 30000) return;
  lastPrimeAt = now;
  const prime = () => {
    const quiet = () => undefined;
    void listServices().catch(quiet);
    void listClients().catch(quiet);
    void getBookingConfig().catch(quiet);
    void getDashboardSummary().catch(quiet);
    void listStockItems().catch(quiet);
  };
  if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(prime, { timeout: 1500 });
    return;
  }
  setTimeout(prime, 0);
}

function persistAuthToken(token) {
  authToken = token;
  if (typeof window !== "undefined") {
    try {
      window.localStorage?.setItem("scheduler.token", token);
    } catch {
      // ignore storage failures
    }
    window.dispatchEvent(new CustomEvent("auth:refreshed", { detail: { token } }));
  }
}

export class OfflineError extends Error {
  constructor(message = "Request unavailable while offline") {
    super(message);
    this.name = "OfflineError";
    this.offline = true;
  }
}

export class NetworkRequestError extends Error {
  constructor(message = "Unable to reach Offlico right now. Please try again.") {
    super(message);
    this.name = "NetworkRequestError";
    this.network = true;
  }
}

export class APIRequestError extends Error {
  constructor({ status, statusText, message, rawMessage = "", responseBody = "" }) {
    super(message || `${status} ${statusText}`.trim());
    this.name = "APIRequestError";
    this.status = status;
    this.statusText = statusText || "";
    this.rawMessage = rawMessage;
    this.responseBody = responseBody;
  }
}

const API_ERROR_FIELD_LABELS = {
  customer_name: "Name",
  customer_email: "Email",
  customer_phone: "Phone",
  address_line1: "Address line 1",
  address_line2: "Address line 2",
  town_city: "City or town",
  postcode: "Postcode",
  preferred_when: "Preferred time",
  notes: "Details",
  password: "Password",
  consent_to_share: "Agreement",
};

function formatValidationIssue(value) {
  if (!value || typeof value !== "object") return "";
  const msg = extractErrorMessage(value.msg || value.message || value.reason);
  if (!msg) return "";
  const loc = Array.isArray(value.loc) ? value.loc : [];
  const field = loc
    .slice()
    .reverse()
    .find((part) => typeof part === "string" && part !== "body" && part !== "query" && part !== "path");
  const label = API_ERROR_FIELD_LABELS[field] || (field ? String(field).replace(/_/g, " ") : "");
  const normalized = msg.toLowerCase();
  let friendly = msg;
  if (normalized.includes("string should have at least") && value.ctx?.min_length) {
    friendly = `must be at least ${value.ctx.min_length} characters.`;
  } else if (normalized.includes("field required") || normalized.includes("missing")) {
    friendly = "is needed.";
  } else if (normalized.includes("value is not a valid email") || normalized.includes("valid email")) {
    friendly = "must be a valid email address.";
  }
  if (!label) return friendly;
  const sentence = friendly.charAt(0).toLowerCase() + friendly.slice(1);
  return `${label} ${sentence}`;
}

function extractErrorMessage(value) {
  if (!value) return "";
  if (typeof value === "string") return value.trim();
  if (Array.isArray(value)) {
    for (const item of value) {
      const nested = extractErrorMessage(item);
      if (nested) return nested;
    }
    return "";
  }
  if (typeof value === "object") {
    return (
      formatValidationIssue(value) ||
      extractErrorMessage(value.detail) ||
      extractErrorMessage(value.msg) ||
      extractErrorMessage(value.message) ||
      extractErrorMessage(value.reason) ||
      extractErrorMessage(value.error) ||
      extractErrorMessage(value.title) ||
      ""
    );
  }
  return "";
}

function parseErrorMessageFromResponseText(text) {
  const trimmed = String(text || "").trim();
  if (!trimmed) return "";
  try {
    const payload = JSON.parse(trimmed);
    return extractErrorMessage(payload) || trimmed;
  } catch {
    return trimmed;
  }
}

function rewriteServerMessage(message, status) {
  const normalized = String(message || "").toLowerCase();
  if (status === 401) {
    if (
      normalized.includes("invalid credentials") ||
      normalized.includes("email or password") ||
      normalized.includes("incorrect password") ||
      normalized.includes("password is incorrect")
    ) {
      return "Incorrect email or password.";
    }
    return "Couldn't verify your session right now. Please try again.";
  }
  if (status === 403) {
    // The server names the cheapest plan that includes the feature ("... on the
    // Basic and Professional plans."): show it as it is.
    if (normalized.includes("basic and professional plans")) {
      return message;
    }
    if (
      normalized.includes("professional plan") ||
      normalized.includes("pro plan") ||
      normalized.includes("upgrade")
    ) {
      return "Professional plan required. Upgrade to continue.";
    }
    if (
      normalized.includes("admin access required") ||
      (normalized.includes("admin") && normalized.includes("permission"))
    ) {
      return "Only an admin can do this action.";
    }
    if (
      normalized.includes("verification required") ||
      normalized.includes("business verification") ||
      normalized.includes("kyc")
    ) {
      return "Finish business verification to continue.";
    }
    if (normalized.includes("stripe") && normalized.includes("onboarding")) {
      return "Complete Stripe onboarding to continue.";
    }
  }
  return message;
}

function isBrowserOffline() {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

function isFetchNetworkError(err) {
  if (!err) return false;
  const message = String(err?.message || "").trim().toLowerCase();
  return (
    err?.name === "TypeError" ||
    message === "failed to fetch" ||
    message.includes("networkerror") ||
    message.includes("load failed")
  );
}

function rethrowFetchError(err, { networkMessage } = {}) {
  if (
    err instanceof OfflineError ||
    err instanceof APIRequestError ||
    err instanceof NetworkRequestError
  ) {
    throw err;
  }
  if (isFetchNetworkError(err)) {
    if (isBrowserOffline()) {
      throw new OfflineError();
    }
    throw new NetworkRequestError(networkMessage);
  }
  throw err;
}

async function throwApiError(res) {
  const text = await res.text().catch(() => "");
  const rawMessage = parseErrorMessageFromResponseText(text);
  const fallback = `${res.status} ${res.statusText}`.trim();
  const finalMessage = rewriteServerMessage(rawMessage || fallback, res.status);
  throw new APIRequestError({
    status: res.status,
    statusText: res.statusText,
    message: finalMessage,
    rawMessage,
    responseBody: text,
  });
}

function annotateFromCache(data, reason = "fallback") {
  if (data == null) return data;
  const copy = Array.isArray(data)
    ? data.map((item) => (typeof item === "object" && item !== null ? { ...item } : item))
    : { ...data };
  Object.defineProperty(copy, "__source", { value: "cache", enumerable: false });
  Object.defineProperty(copy, "__cacheReason", { value: reason, enumerable: false });
  return copy;
}

const CACHE_KEYS = {
  authMe: "auth.me.v1",
  professionProfile: "profession.profile.v1",
  billingStatus: "billing.status.v1",
  businessVerificationStatus: "businessVerification.status.v1",
  dashboardSummary: "dashboard.summary.v1",
  dashboardActivity: "dashboard.activity.v1",
  bookingConfig: "booking.config.v1",
  bookingRecentContacts: "booking.recentContacts.v1",
  bookingRecentAppointments: "booking.recentAppointments.v1",
  bookingRecentEnquiries: "booking.recentEnquiries.v1",
  timelineToday: "timeline.today.v5",
  clients: "clients.v1",
  services: "services.v1",
  stockItems: "stockItems.v1",
  documents: "documents.v1",
  invoicesList: "invoices.list.v1",
  paymentSettings: "payments.settings.v1",
  paymentOperations: "payments.operations.v1",
  paymentRequestHistory: "payments.requestHistory.v1",
  clientLocations: "clientLocations.v1",
};

const PROFILE_CACHE_TTL_MS = 5 * 60 * 1000;
const BILLING_CACHE_TTL_MS = 5 * 60 * 1000;
const REFERENCE_CACHE_TTL_MS = 5 * 60 * 1000;
const DEFAULT_CACHED_GET_TTL_MS = 5 * 60 * 1000;
const CACHE_REVALIDATE_INTERVAL_MS = 30 * 1000;

function clientLocationsCacheKey(clientId) {
  if (clientId == null || clientId === "") {
    return CACHE_KEYS.clientLocations;
  }
  return `${CACHE_KEYS.clientLocations}.${clientId}`;
}

function cacheScope() {
  const tokenScope = authToken ? `token-${String(authToken).slice(-16)}` : null;
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      const rawUser = window.localStorage.getItem("scheduler.user");
      if (rawUser) {
        const parsed = JSON.parse(rawUser);
        const userId = parsed?.id;
        if (userId != null && userId !== "") {
          return tokenScope ? `user-${userId}.${tokenScope}` : `user-${userId}`;
        }
      }
    } catch {
      // ignore storage/parse issues and fall back to token scope
    }
  }
  return tokenScope || "anon";
}

function scopedCacheKey(baseKey) {
  return `${baseKey}.${cacheScope()}`;
}

function writeScopedCache(baseKey, payload) {
  if (payload == null) return;
  saveCache(scopedCacheKey(baseKey), payload);
}

function clearScopedCache(baseKey, scope = cacheScope()) {
  const key = `${baseKey}.${scope}`;
  removeCache(key);
  lastCacheNetworkFetchAt.delete(key);
}

function clearScopedCacheFamily(baseKey, scope = cacheScope()) {
  clearScopedCache(baseKey, scope);
  if (typeof localStorage === "undefined") return;
  const prefix = `${CACHE_STORAGE_PREFIX}${baseKey}.`;
  const suffix = `.${scope}`;
  const keysToRemove = [];
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (key && key.startsWith(prefix) && key.endsWith(suffix)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));
}

function readScopedCacheData(baseKey) {
  const envelope = readCacheEnvelope(scopedCacheKey(baseKey));
  return envelope?.data ?? null;
}

export function getCachedServicesSnapshot() {
  const data = readScopedCacheData(CACHE_KEYS.services);
  return Array.isArray(data) ? data : [];
}

export function getCachedStockSnapshot() {
  const data = readScopedCacheData(CACHE_KEYS.stockItems);
  return Array.isArray(data) ? data : [];
}

export function getCachedClientsSnapshot() {
  const data = readScopedCacheData(CACHE_KEYS.clients);
  return Array.isArray(data) ? data : [];
}

export function getCachedClientLocationsSnapshot(clientId) {
  const data = readScopedCacheData(clientLocationsCacheKey(clientId));
  return Array.isArray(data) ? data : [];
}

export function getCachedBookingConfigSnapshot() {
  const data = readScopedCacheData(CACHE_KEYS.bookingConfig);
  return data && typeof data === "object" ? data : null;
}

function clearDashboardCaches() {
  clearScopedCache(CACHE_KEYS.dashboardSummary);
  [5, 10, 20, 25, 50].forEach((limit) => clearScopedCache(`${CACHE_KEYS.dashboardActivity}.${limit}`));
}

function clearCalendarRangeCaches() {
  if (typeof localStorage === "undefined") return;
  const prefix = `${CALENDAR_RANGE_CACHE_STORAGE_PREFIX}.`;
  const keysToRemove = [];
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (key && key.startsWith(prefix)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));
}

function clearAppointmentRelatedCaches() {
  clearScopedCacheFamily(CACHE_KEYS.timelineToday);
  clearDashboardCaches();
  clearScopedCache(CACHE_KEYS.bookingRecentAppointments);
  clearCalendarRangeCaches();
}

function clearClientRelatedCaches() {
  clearScopedCache(CACHE_KEYS.clients);
  clearScopedCacheFamily(CACHE_KEYS.timelineToday);
}

function timelineCacheKey(date) {
  return `${CACHE_KEYS.timelineToday}.${date || "today"}`;
}

function invoiceListCacheKey(params = {}) {
  const search = new URLSearchParams();
  if (params.status) search.set("status", params.status);
  if (params.date_from) search.set("date_from", params.date_from);
  if (params.date_to) search.set("date_to", params.date_to);
  if (params.search) search.set("search", params.search);
  const qs = search.toString();
  return `${CACHE_KEYS.invoicesList}.${qs || "all"}`;
}

function clearInvoiceCaches() {
  clearScopedCacheFamily(CACHE_KEYS.invoicesList);
}

function clearPaymentActivityCaches() {
  clearScopedCache(CACHE_KEYS.paymentOperations);
  clearScopedCache(CACHE_KEYS.paymentRequestHistory);
}

async function cachedGetWithBootstrap(path, baseKey, maxAgeMs, options = {}) {
  const key = scopedCacheKey(baseKey);
  const envelope = readCacheEnvelope(key);
  const cachedData = envelope?.data;
  const savedAt = Number(envelope?.savedAt || 0);
  const hasFreshCache =
    cachedData != null && Number.isFinite(savedAt) && savedAt > 0 && Date.now() - savedAt <= maxAgeMs;
  const preferFresh = options?.preferFresh === true;

  if (hasFreshCache && !preferFresh) {
    const lastNetworkFetchAt = Number(lastCacheNetworkFetchAt.get(key) || 0);
    if (Date.now() - lastNetworkFetchAt >= CACHE_REVALIDATE_INTERVAL_MS) {
      lastCacheNetworkFetchAt.set(key, Date.now());
      void apiFetch(path)
        .then((payload) => {
          if (payload != null) saveCache(key, payload);
          if (typeof options?.onRevalidate === "function") {
            options.onRevalidate(payload);
          }
        })
        .catch(() => {
          // Silent background refresh failure: keep cached UI stable.
        });
    }
    return annotateFromCache(cachedData, "bootstrap");
  }

  try {
    const payload = await apiFetch(path);
    if (payload != null) {
      saveCache(key, payload);
      lastCacheNetworkFetchAt.set(key, Date.now());
    }
    return payload;
  } catch (err) {
    if (cachedData != null && (err?.offline || err?.network)) {
      return annotateFromCache(cachedData, "fallback");
    }
    throw err;
  }
}

function inFlightGetKey(path, options = {}) {
  const method = String(options.method || "GET").toUpperCase();
  const hasCustomFetchOptions = Object.keys(options).some((key) => ![
    "method",
    "networkMessage",
    "skipAuthInvalid",
    "skipAuthRefresh",
  ].includes(key));
  if (method !== "GET" || options.returnResponse || hasCustomFetchOptions) return "";
  return JSON.stringify([
    authToken || "anonymous",
    path,
    options.networkMessage || "",
    Boolean(options.skipAuthInvalid),
    Boolean(options.skipAuthRefresh),
  ]);
}

/**
 * Authenticated request against the Offlico API (bearer token, one refresh +
 * retry on 401, the resolved API base, Offline/Network/APIRequestError errors).
 * Exported for feature modules with their own API clients (src/aiNotes/api.js).
 * `body` may be raw bytes: pass your own Content-Type in `headers`.
 */
export function apiRequest(path, options = {}) {
  return apiFetch(path, options);
}

function apiFetch(path, options = {}) {
  const requestKey = inFlightGetKey(path, options);
  if (!requestKey) return performApiFetch(path, options);

  const existingRequest = inFlightJsonGets.get(requestKey);
  if (existingRequest) return existingRequest;

  const request = performApiFetch(path, options).finally(() => {
    if (inFlightJsonGets.get(requestKey) === request) {
      inFlightJsonGets.delete(requestKey);
    }
  });
  inFlightJsonGets.set(requestKey, request);
  return request;
}

async function performApiFetch(path, options = {}) {
  const {
    skipAuthInvalid,
    skipAuthRefresh,
    networkMessage,
    returnResponse = false,
    networkRetryCount = 0,
    headers: customHeaders,
    ...fetchOptions
  } = options;
  try {
    const res = await fetch(`${CONFIG.BASE}${path}`, {
      ...fetchOptions,
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        ...(customHeaders || {}),
      },
      credentials: "include",
    });

    if (!res.ok) {
      if (res.status === 401 && !skipAuthRefresh && typeof window !== "undefined") {
        try {
          const refreshed = await refreshSession();
          if (refreshed?.access_token) {
            return performApiFetch(path, { ...options, skipAuthRefresh: true });
          }
        } catch {
          // fall through to auth invalid handling
        }
      }
      await throwApiError(res);
    }

    // Some endpoints might return empty
    if (res.status === 204) return returnResponse ? { data: null, response: res } : null;
    const ct = res.headers.get("content-type") || "";
    if (ct.includes("application/json")) {
      const text = await res.text().catch(() => "");
      if (!text) return returnResponse ? { data: null, response: res } : null;
      const data = JSON.parse(text);
      return returnResponse ? { data, response: res } : data;
    }
    return returnResponse ? { data: null, response: res } : null;
  } catch (err) {
    const method = String(fetchOptions.method || "GET").toUpperCase();
    if (
      method === "GET" &&
      networkRetryCount < 1 &&
      isFetchNetworkError(err) &&
      !isBrowserOffline()
    ) {
      await new Promise((resolve) => setTimeout(resolve, SAFE_GET_RETRY_DELAY_MS));
      return performApiFetch(path, { ...options, networkRetryCount: networkRetryCount + 1 });
    }
    rethrowFetchError(err, { networkMessage });
  }
}

async function apiFormFetch(path, formData, options = {}) {
  const {
    skipAuthInvalid,
    skipAuthRefresh,
    networkMessage,
    headers: customHeaders,
    ...fetchOptions
  } = options;
  try {
    const res = await fetch(`${CONFIG.BASE}${path}`, {
      ...fetchOptions,
      headers: {
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        ...(customHeaders || {}),
      },
      credentials: "include",
      body: formData,
    });

    if (!res.ok) {
      if (res.status === 401 && !skipAuthRefresh && typeof window !== "undefined") {
        try {
          const refreshed = await refreshSession();
          if (refreshed?.access_token) {
            return apiFormFetch(path, formData, { ...options, skipAuthRefresh: true });
          }
        } catch {
          // fall through to auth invalid handling
        }
      }
      await throwApiError(res);
    }

    if (res.status === 204) return null;
    const ct = res.headers.get("content-type") || "";
    if (ct.includes("application/json")) {
      const text = await res.text().catch(() => "");
      if (!text) return null;
      return JSON.parse(text);
    }
    return null;
  } catch (err) {
    rethrowFetchError(err, { networkMessage });
  }
}

async function apiBlobFetch(path, options = {}) {
  const {
    skipAuthInvalid,
    skipAuthRefresh,
    headers: customHeaders,
    ...fetchOptions
  } = options;
  try {
    const res = await fetch(`${CONFIG.BASE}${path}`, {
      ...fetchOptions,
      headers: {
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        ...(customHeaders || {}),
      },
      credentials: "include",
    });

    if (!res.ok) {
      if (res.status === 401 && !skipAuthRefresh && typeof window !== "undefined") {
        try {
          const refreshed = await refreshSession();
          if (refreshed?.access_token) {
            return apiBlobFetch(path, { ...options, skipAuthRefresh: true });
          }
        } catch {
          // fall through to auth invalid handling
        }
      }
      await throwApiError(res);
    }

    return res.blob();
  } catch (err) {
    rethrowFetchError(err);
  }
}

function safeDownloadFilename(value, fallback = "download") {
  return String(value || fallback)
    .replace(/[^a-z0-9._-]+/gi, "-")
    .replace(/^-+|-+$/g, "") || fallback;
}

export async function submitContactMessage(payload) {
  return apiFetch("/public/contact", {
    method: "POST",
    body: JSON.stringify(payload || {}),
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function getPublicBlogPosts({ limit = 12, category = "", q = "" } = {}) {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  if (category) params.set("category", category);
  if (q) params.set("q", q);
  return apiFetch(`/public/blog/posts?${params.toString()}`, {
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function getPublicBlogPost(slug) {
  return apiFetch(`/public/blog/posts/${encodeURIComponent(slug)}`, {
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function getDirectoryCatalog() {
  return apiFetch("/directory/catalog", {
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function getDirectoryProfession(slug, params = {}) {
  const search = new URLSearchParams();
  if (params.postcode) search.set("postcode", params.postcode);
  if (params.location) search.set("location", params.location);
  if (params.limit) search.set("limit", String(params.limit));
  if (params.offset) search.set("offset", String(params.offset));
  if (params.max_distance_miles) search.set("max_distance_miles", String(params.max_distance_miles));
  if (params.source) search.set("source", String(params.source));
  if (params.find_more) search.set("find_more", "true");
  if (params.token) search.set("token", String(params.token));
  if (params.managed_profile_id) search.set("managed_profile_id", String(params.managed_profile_id));
  const qs = search.toString();
  return apiFetch(`/directory/professions/${encodeURIComponent(slug)}${qs ? `?${qs}` : ""}`, {
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function submitCustomerDemandLead(payload = {}) {
  return apiFetch("/directory/looking-for", {
    method: "POST",
    body: JSON.stringify(payload || {}),
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function setCustomerLeadPassword(payload = {}) {
  return apiFetch("/directory/customer-account/set-password", {
    method: "POST",
    body: JSON.stringify(payload || {}),
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function registerCustomerHub(payload = {}) {
  return apiFetch("/directory/customer-account/register", {
    method: "POST",
    body: JSON.stringify(payload || {}),
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function loginCustomerHub(payload = {}) {
  return apiFetch("/directory/customer-account/login", {
    method: "POST",
    body: JSON.stringify(payload || {}),
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function openCustomerHubSession() {
  return apiFetch("/directory/customer-account/session", {
    method: "POST",
  });
}

export async function getCustomerLeadHub(token) {
  const params = new URLSearchParams({ token: String(token || "") });
  return apiFetch(`/directory/customer-hub?${params.toString()}`, {
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function updateCustomerHubAccount(payload = {}) {
  return apiFetch("/directory/customer-hub/account", {
    method: "PATCH",
    body: JSON.stringify(payload || {}),
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function uploadCustomerHubAccountPhoto(token, file) {
  const formData = new FormData();
  formData.append("token", String(token || ""));
  formData.append("file", file);
  return apiFormFetch("/directory/customer-hub/account/photo", formData, {
    method: "POST",
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function deleteCustomerHubAccountPhoto(token) {
  const params = new URLSearchParams({ token: String(token || "") });
  return apiFetch(`/directory/customer-hub/account/photo?${params.toString()}`, {
    method: "DELETE",
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function updateCustomerHubRequest(leadId, payload = {}) {
  return apiFetch(`/directory/customer-hub/requests/${encodeURIComponent(leadId)}`, {
    method: "PATCH",
    body: JSON.stringify(payload || {}),
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function cancelCustomerHubRequest(leadId, payload = {}) {
  return apiFetch(`/directory/customer-hub/requests/${encodeURIComponent(leadId)}/cancel`, {
    method: "POST",
    body: JSON.stringify(payload || {}),
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function requestCustomerHubRequestAgain(leadId, payload = {}) {
  return apiFetch(`/directory/customer-hub/requests/${encodeURIComponent(leadId)}/request-again`, {
    method: "POST",
    body: JSON.stringify(payload || {}),
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function deleteCustomerHubRequest(leadId, token) {
  const params = new URLSearchParams({ token: token || "" });
  return apiFetch(`/directory/customer-hub/requests/${encodeURIComponent(leadId)}?${params.toString()}`, {
    method: "DELETE",
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function confirmCustomerHubBooking(appointmentId, payload = {}) {
  return apiFetch(`/directory/customer-hub/bookings/${encodeURIComponent(appointmentId)}/confirm`, {
    method: "POST",
    body: JSON.stringify(payload || {}),
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function createCustomerHubBookingCheckout(appointmentId, payload = {}) {
  return apiFetch(`/directory/customer-hub/bookings/${encodeURIComponent(appointmentId)}/checkout`, {
    method: "POST",
    body: JSON.stringify(payload || {}),
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function cancelCustomerHubBooking(appointmentId, payload = {}) {
  return apiFetch(`/directory/customer-hub/bookings/${encodeURIComponent(appointmentId)}/cancel`, {
    method: "POST",
    body: JSON.stringify(payload || {}),
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function requestCustomerHubBookingAgain(appointmentId, payload = {}) {
  return apiFetch(`/directory/customer-hub/bookings/${encodeURIComponent(appointmentId)}/request-again`, {
    method: "POST",
    body: JSON.stringify(payload || {}),
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function createCustomerHubManualBooking(payload = {}) {
  return apiFetch("/directory/customer-hub/bookings/manual", {
    method: "POST",
    body: JSON.stringify(payload || {}),
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function updateCustomerHubManualBooking(appointmentId, payload = {}) {
  return apiFetch(`/directory/customer-hub/bookings/${encodeURIComponent(appointmentId)}/manual`, {
    method: "PATCH",
    body: JSON.stringify(payload || {}),
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function deleteCustomerHubManualBooking(appointmentId, token) {
  const params = new URLSearchParams({ token: String(token || "") });
  return apiFetch(`/directory/customer-hub/bookings/${encodeURIComponent(appointmentId)}/manual?${params.toString()}`, {
    method: "DELETE",
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function createCustomerHubProfile(payload = {}) {
  return apiFetch("/directory/customer-hub/profiles", {
    method: "POST",
    body: JSON.stringify(payload || {}),
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function updateCustomerHubProfile(profileId, payload = {}) {
  return apiFetch(`/directory/customer-hub/profiles/${encodeURIComponent(profileId)}`, {
    method: "PATCH",
    body: JSON.stringify(payload || {}),
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function deleteCustomerHubProfile(profileId, token) {
  const params = new URLSearchParams({ token: String(token || "") });
  return apiFetch(`/directory/customer-hub/profiles/${encodeURIComponent(profileId)}?${params.toString()}`, {
    method: "DELETE",
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function saveCustomerHubProfessional(payload = {}) {
  return apiFetch("/directory/customer-hub/saved-professionals", {
    method: "POST",
    body: JSON.stringify(payload || {}),
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function unsaveCustomerHubProfessional(userId, token) {
  const params = new URLSearchParams({ token: String(token || "") });
  return apiFetch(`/directory/customer-hub/saved-professionals/${encodeURIComponent(userId)}?${params.toString()}`, {
    method: "DELETE",
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function updateCustomerHubProfessionalInteraction(payload = {}) {
  return apiFetch("/directory/customer-hub/professional-interactions", {
    method: "POST",
    body: JSON.stringify(payload || {}),
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function requestCustomerHubEmailCode(payload = {}) {
  return apiFetch("/directory/customer-hub/email/request", {
    method: "POST",
    body: JSON.stringify(payload || {}),
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function verifyCustomerHubEmailCode(payload = {}) {
  return apiFetch("/directory/customer-hub/email/verify", {
    method: "POST",
    body: JSON.stringify(payload || {}),
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function requestCustomerHubPhoneCode(payload = {}) {
  return apiFetch("/directory/customer-hub/phone/request", {
    method: "POST",
    body: JSON.stringify(payload || {}),
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function verifyCustomerHubPhoneCode(payload = {}) {
  return apiFetch("/directory/customer-hub/phone/verify", {
    method: "POST",
    body: JSON.stringify(payload || {}),
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function listProfessionalLeads({ maxDistanceMiles = 60, maxDistanceMinutes = null } = {}) {
  const params = new URLSearchParams({
    max_distance_miles: String(maxDistanceMiles),
  });
  if (maxDistanceMinutes != null) {
    params.set("max_distance_minutes", String(maxDistanceMinutes));
  }
  return apiFetch(`/directory/professional/leads?${params.toString()}`);
}

export async function getProfessionalLeadWallet() {
  return apiFetch("/directory/professional/leads/wallet");
}

export async function topUpProfessionalLeadWallet(amountPence) {
  return apiFetch("/directory/professional/leads/wallet/top-up", {
    method: "POST",
    body: JSON.stringify({ amount_pence: amountPence }),
  });
}

export async function unlockProfessionalLead(leadId) {
  return apiFetch(`/directory/professional/leads/${leadId}/unlock`, {
    method: "POST",
  });
}

export async function declineProfessionalLead(leadId, payload = {}) {
  return apiFetch(`/directory/professional/leads/${leadId}/decline`, {
    method: "POST",
    body: JSON.stringify(payload || {}),
  });
}

export async function updateProfessionalLeadConversion(leadId, conversionStatus) {
  return apiFetch(`/directory/professional/leads/${leadId}/conversion`, {
    method: "POST",
    body: JSON.stringify({ conversion_status: conversionStatus }),
  });
}

export async function complainAboutProfessionalLead(leadId, reason) {
  return apiFetch(`/directory/professional/leads/${leadId}/complaint`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export async function submitBusinessVerificationRequest(payload) {
  return apiFetch("/business-verification/request", {
    method: "POST",
    body: JSON.stringify(payload || {}),
  }).then((response) => {
    clearScopedCache(CACHE_KEYS.businessVerificationStatus);
    return response;
  });
}

export async function submitBugReport(payload = {}) {
  const title = String(payload.title || "").trim();
  const details = String(payload.details || "").trim();
  const files = Array.isArray(payload.files) ? payload.files.filter(Boolean) : [];

  if (!title) throw new Error("Issue title is required.");
  if (!details) throw new Error("Issue details are required.");

  const formData = new FormData();
  formData.append("title", title);
  formData.append("details", details);
  formData.append("report_type", payload.report_type || "bug");
  formData.append("source", payload.source || "web");
  if (payload.page_url) formData.append("page_url", String(payload.page_url));
  if (payload.screen_name) formData.append("screen_name", String(payload.screen_name));
  if (payload.app_version) formData.append("app_version", String(payload.app_version));
  if (payload.device_info) formData.append("device_info", String(payload.device_info));
  files.forEach((file) => {
    formData.append("files", file, file.name || "attachment");
  });

  return apiFormFetch("/bug-reports", formData, { method: "POST" });
}

export async function getBusinessVerificationRequest() {
  return apiFetch("/business-verification/request");
}

export async function getBusinessVerificationStatus(options = {}) {
  return cachedGetWithBootstrap(
    "/business-verification/status",
    CACHE_KEYS.businessVerificationStatus,
    REFERENCE_CACHE_TTL_MS,
    {
      preferFresh: options?.forceRefresh === true || options?.preferCache !== true,
      onRevalidate: options?.onRevalidate,
    },
  );
}

export async function getTeamSchedulingStatus() {
  return apiFetch("/team-scheduling/status");
}

export async function listTeamSchedulingTeams() {
  return apiFetch("/team-scheduling/teams");
}

export async function createTeamSchedulingTeam(payload) {
  return apiFetch("/team-scheduling/teams", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function listTeamSchedulingMemberships(teamId) {
  return apiFetch(`/team-scheduling/teams/${teamId}/memberships`);
}

export async function updateTeamSchedulingFinancialPermissions(teamId, membershipId, payload) {
  return apiFetch(`/team-scheduling/teams/${teamId}/memberships/${membershipId}/financial-permissions`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function createTeamSchedulingInvite(teamId, payload) {
  return apiFetch(`/team-scheduling/teams/${teamId}/invites`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function listTeamSchedulingInvites(teamId) {
  return apiFetch(`/team-scheduling/teams/${teamId}/invites`);
}

export async function acceptTeamSchedulingInvite(token) {
  return apiFetch(`/team-scheduling/invites/${encodeURIComponent(token)}/accept`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function declineTeamSchedulingInvite(token) {
  return apiFetch(`/team-scheduling/invites/${encodeURIComponent(token)}/decline`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function getTeamSchedulingCalendar(teamId, { startDate, endDate }) {
  const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
  return apiFetch(`/team-scheduling/teams/${teamId}/calendar?${params.toString()}`);
}

export async function assignTeamSchedulingAppointment(teamId, appointmentId, payload) {
  return apiFetch(`/team-scheduling/teams/${teamId}/appointments/${appointmentId}/assignment`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function listTeamSchedulingBookingRequests(teamId) {
  return apiFetch(`/team-scheduling/teams/${teamId}/booking-requests`);
}

export async function listTeamSchedulingStaffAppointments(teamId) {
  const params = new URLSearchParams({ team_id: String(teamId) });
  return apiFetch(`/team-scheduling/staff/appointments?${params.toString()}`);
}

export async function updateTeamSchedulingStaffAppointment(teamId, appointmentId, payload) {
  const params = new URLSearchParams({ team_id: String(teamId) });
  return apiFetch(`/team-scheduling/staff/appointments/${appointmentId}?${params.toString()}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function getTeamSchedulingAppointmentPayment(teamId, appointmentId) {
  return apiFetch(`/team-scheduling/teams/${teamId}/appointments/${appointmentId}/payment-operation`);
}

export async function createTeamSchedulingPaymentRequest(teamId, appointmentId, payload) {
  return apiFetch(`/team-scheduling/teams/${teamId}/appointments/${appointmentId}/payment-requests`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function createTeamSchedulingRefund(teamId, appointmentId, payload) {
  return apiFetch(`/team-scheduling/teams/${teamId}/appointments/${appointmentId}/refunds`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getTeamSchedulingBookingRequestSuggestions(teamId, requestId) {
  return apiFetch(`/team-scheduling/teams/${teamId}/booking-requests/${requestId}/suggestions`);
}

export async function acceptTeamSchedulingBookingRequest(teamId, requestId, payload = {}) {
  return apiFetch(`/team-scheduling/teams/${teamId}/booking-requests/${requestId}/accept`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function declineTeamSchedulingBookingRequest(teamId, requestId, payload = {}) {
  return apiFetch(`/team-scheduling/teams/${teamId}/booking-requests/${requestId}/decline`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function listTeamSchedulingExternalBusyBlocks(teamId) {
  return apiFetch(`/team-scheduling/teams/${teamId}/external-busy-blocks`);
}

export async function createTeamSchedulingExternalBusyBlock(teamId, payload) {
  return apiFetch(`/team-scheduling/teams/${teamId}/external-busy-blocks`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateTeamSchedulingStaffProfile(teamId, payload) {
  return apiFetch(`/team-scheduling/teams/${teamId}/staff-profile`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function getTeamSchedulingRolloutReadiness(teamId) {
  return apiFetch(`/team-scheduling/teams/${teamId}/rollout-readiness`);
}

export async function listTeamSchedulingServiceStaffRules(teamId) {
  return apiFetch(`/team-scheduling/teams/${teamId}/service-rules`);
}

export async function getTeamSchedulingServiceStaffRules(teamId, serviceId) {
  return apiFetch(`/team-scheduling/teams/${teamId}/services/${serviceId}/staff-rules`);
}

export async function updateTeamSchedulingServiceStaffRules(teamId, serviceId, payload) {
  return apiFetch(`/team-scheduling/teams/${teamId}/services/${serviceId}/staff-rules`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function listTeamSchedulingNotifications(teamId) {
  return apiFetch(`/team-scheduling/teams/${teamId}/notifications`);
}

export async function markTeamSchedulingNotificationRead(teamId, notificationId) {
  return apiFetch(`/team-scheduling/teams/${teamId}/notifications/${notificationId}/read`, {
    method: "PATCH",
  });
}

let refreshPromise = null;

async function refreshSession() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = fetch(`${CONFIG.BASE}/auth/refresh`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
  })
    .then(async (res) => {
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status} ${res.statusText} ${text}`.trim());
      }
      return res.json();
    })
    .then((payload) => {
      if (payload?.access_token) {
        persistAuthToken(payload.access_token);
      }
      if (typeof window !== "undefined" && payload?.user) {
        window.dispatchEvent(
          new CustomEvent("auth:refreshed", { detail: { token: payload.access_token, user: payload.user } })
        );
      }
      return payload;
    })
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

export function restoreAuthSession() {
  return refreshSession();
}

async function cachedGet(path, cacheKey, options = {}) {
  if (!cacheKey) {
    return apiFetch(path);
  }
  const maxAgeMs = Number(options?.maxAgeMs);
  return cachedGetWithBootstrap(
    path,
    cacheKey,
    Number.isFinite(maxAgeMs) && maxAgeMs > 0 ? maxAgeMs : DEFAULT_CACHED_GET_TTL_MS,
  );
}

function queueOfflineMutation(path, options, meta = {}) {
  const action = enqueueOfflineAction({
    path,
    method: options?.method || "POST",
    body: options?.body || null,
    headers: options?.headers || {},
    meta,
  });
  if (meta?.timelinePatch) {
    recordTimelinePatch({
      actionId: action.id,
      appointment_id: meta.timelinePatch.appointment_id,
      changes: meta.timelinePatch.changes,
      reason: meta.timelinePatch.reason,
    });
  }
  return { queued: true, actionId: action.id };
}

async function queueableMutation(path, options, meta) {
  try {
    return await apiFetch(path, options);
  } catch (err) {
    const method = String(options?.method || "POST").toUpperCase();
    const replaySafeAfterNetworkFailure = method === "PUT" || method === "PATCH";
    if (err?.offline || (err?.network && replaySafeAfterNetworkFailure)) {
      return queueOfflineMutation(path, options, meta);
    }
    throw err;
  }
}

export async function syncOfflineQueue() {
  const result = await flushOfflineQueue(async (entry) =>
    apiFetch(entry.path, {
      method: entry.method,
      body: entry.body,
      headers: entry.headers,
      skipAuthInvalid: true,
    }),
  );
  if (result.processedIds?.length) {
    removeTimelinePatchesForActions(result.processedIds);
  }
  return result;
}

export function getOfflineQueueInfo() {
  return getOfflineMeta();
}

// auth
export function register(payload) {
  return apiFetch(`/auth/register`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function login(payload) {
  return apiFetch(`/auth/login`, {
    method: "POST",
    body: JSON.stringify(payload),
    credentials: "include",
    skipAuthRefresh: true,
  });
}

export function getGoogleOAuthStartUrl(appReturn) {
  const query = new URLSearchParams({ app_return: appReturn });
  return apiFetch(`/auth/oauth/google/start?${query.toString()}`, {
    method: "GET",
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export function completeGoogleOAuth(payload) {
  return apiFetch(`/auth/oauth/google/complete`, {
    method: "POST",
    body: JSON.stringify(payload),
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export function completeAppleOAuth(payload) {
  return apiFetch(`/auth/oauth/apple`, {
    method: "POST",
    body: JSON.stringify(payload),
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export function requestSmsCode(payload) {
  return apiFetch(`/auth/sms/request`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function verifySmsCode(payload) {
  return apiFetch(`/auth/sms/verify`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function requestPasswordReset(payload) {
  return apiFetch(`/auth/password/forgot`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function requestOwnPasswordReset() {
  return apiFetch(`/auth/password/forgot/me`, {
    method: "POST",
  });
}

export function validatePasswordResetToken(payload) {
  return apiFetch(`/auth/password/reset/validate`, {
    method: "POST",
    body: JSON.stringify(payload),
    skipAuthRefresh: true,
  });
}

export function confirmPasswordReset(payload) {
  return apiFetch(`/auth/password/reset`, {
    method: "POST",
    body: JSON.stringify(payload),
    skipAuthRefresh: true,
  });
}

export function requestEmailVerification() {
  return apiFetch(`/auth/email/request`, {
    method: "POST",
  });
}

export function confirmEmailVerification(payload) {
  return apiFetch(`/auth/email/confirm`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function requestAccountContactChange(payload) {
  return apiFetch(`/auth/contact-change/request`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function confirmAccountContactChange(payload) {
  return apiFetch(`/auth/contact-change/confirm`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function requestAccountContactVerification(payload) {
  return apiFetch(`/auth/contact-verification/request`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function confirmAccountContactVerification(payload) {
  return apiFetch(`/auth/contact-verification/confirm`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function logout() {
  const scope = cacheScope();
  return apiFetch(`/auth/logout`, {
    method: "POST",
    credentials: "include",
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  }).finally(() => {
    clearScopedCache(CACHE_KEYS.authMe, scope);
    clearScopedCache(CACHE_KEYS.professionProfile, scope);
    clearScopedCache(CACHE_KEYS.billingStatus, scope);
  });
}

export async function fetchMe() {
  const payload = await apiFetch(`/auth/me`);
  writeScopedCache(CACHE_KEYS.authMe, payload);
  return payload;
}

export function getAppOnboardingState() {
  return apiFetch(`/auth/onboarding`);
}


export function saveAppOnboardingProgress(flow, payload) {
  return apiFetch(`/auth/onboarding/${encodeURIComponent(flow)}/progress`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}


export function completeAppOnboardingFlow(flow) {
  return apiFetch(`/auth/onboarding/${encodeURIComponent(flow)}`, {
    method: "PATCH",
  });
}

export function updateUserProfile(payload) {
  return queueableMutation(
    `/auth/me`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    },
    { action: "update-profile" },
  );
}

export async function listApiKeys(page = 1, perPage = 25) {
  const result = await apiFetch(`/api-keys?page=${page}&per_page=${perPage}`, {
    returnResponse: true,
  });
  const keys = Array.isArray(result?.data) ? result.data : [];
  const total = Number(result?.response?.headers?.get?.("x-total-count"));
  const responsePage = Number(result?.response?.headers?.get?.("x-page"));
  const responsePerPage = Number(result?.response?.headers?.get?.("x-per-page"));
  return {
    keys,
    total: Number.isFinite(total) ? total : keys.length,
    page: Number.isFinite(responsePage) ? responsePage : page,
    perPage: Number.isFinite(responsePerPage) ? responsePerPage : perPage,
  };
}

export function createApiKey(payload) {
  return apiFetch(`/api-keys`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateApiKey(apiKeyId, payload) {
  return apiFetch(`/api-keys/${apiKeyId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function revokeAllApiKeys(payload) {
  return apiFetch(`/api-keys/revoke-all`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function revokeApiKey(apiKeyId, payload) {
  return apiFetch(`/api-keys/${apiKeyId}/revoke`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// calendar sharing
export function listCalendarShares() {
  return apiFetch(`/calendar-shares`);
}

export function createCalendarShare(payload) {
  return apiFetch(`/calendar-shares`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateCalendarShare(shareId, payload) {
  return apiFetch(`/calendar-shares/${shareId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function createCalendarShareInvite(shareId, payload) {
  return apiFetch(`/calendar-shares/${shareId}/invites`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function revokeCalendarShare(shareId) {
  return apiFetch(`/calendar-shares/${shareId}/revoke`, {
    method: "POST",
  });
}

export function deleteCalendarShare(shareId) {
  return apiFetch(`/calendar-shares/${shareId}`, {
    method: "DELETE",
  });
}

export function revokeCalendarShareInvite(inviteId) {
  return apiFetch(`/calendar-shares/invites/${inviteId}/revoke`, {
    method: "POST",
  });
}

export function revokeCalendarShareViewer(viewerAccessId) {
  return apiFetch(`/calendar-shares/viewers/${viewerAccessId}/revoke`, {
    method: "POST",
  });
}

export function getCalendarShareInvite(token) {
  return apiFetch(`/calendar-shares/invites/${encodeURIComponent(token)}`);
}

export function acceptCalendarShareInvite(token) {
  return apiFetch(`/calendar-shares/invites/${encodeURIComponent(token)}/accept`, {
    method: "POST",
  });
}

export function getSharedCalendar(shareSlug) {
  return apiFetch(`/calendar-shares/shared/${encodeURIComponent(shareSlug)}`);
}

export function getSharedCalendarSubscription(shareSlug) {
  return apiFetch(`/calendar-shares/shared/${encodeURIComponent(shareSlug)}/subscription`);
}

export function getSharedCalendarAppointments(shareSlug, start, end) {
  return apiFetch(
    `/calendar-shares/shared/${encodeURIComponent(shareSlug)}/appointments?start=${start}&end=${end}`
  );
}

// banking
export function listBankProviders() {
  return apiFetch(`/banking/providers`);
}

export function getBankingOverview() {
  return apiFetch(`/banking/overview`);
}

export function listProviderBanks(providerId) {
  return apiFetch(`/banking/providers/${providerId}/banks`);
}

export function listBankConnections() {
  return apiFetch(`/banking/connections`);
}

export function deleteBankConnection(connectionId) {
  return apiFetch(`/banking/connections/${connectionId}`, {
    method: "DELETE",
  });
}

export function listBankTransactionAllocations(transactionId) {
  return apiFetch(`/banking/transactions/${transactionId}/allocations`);
}

export function allocateBankTransaction(transactionId, payload) {
  return apiFetch(`/banking/transactions/${transactionId}/allocations`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function startBankConnect(payload) {
  return apiFetch(`/banking/connect`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function syncBankConnection(connectionId) {
  return apiFetch(`/banking/connections/${connectionId}/sync`, {
    method: "POST",
  });
}

export function listBankAccounts() {
  return apiFetch(`/banking/accounts`);
}

export function deleteBankAccount(accountId) {
  return apiFetch(`/banking/accounts/${accountId}`, {
    method: "DELETE",
  });
}

export function listBankTransactions(params = {}) {
  const query = new URLSearchParams(params).toString();
  return apiFetch(`/banking/transactions${query ? `?${query}` : ""}`);
}

export function createManualBankTransaction(payload) {
  return apiFetch(`/banking/transactions/manual`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateBankTransaction(transactionId, payload) {
  return apiFetch(`/banking/transactions/${transactionId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function deleteBankTransaction(transactionId) {
  return apiFetch(`/banking/transactions/${transactionId}`, {
    method: "DELETE",
  });
}

export function categorizeBankTransaction(transactionId, payload) {
  return apiFetch(`/banking/transactions/${transactionId}/categorize`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateBankTransactionCategorization(transactionId, payload) {
  return apiFetch(`/banking/transactions/${transactionId}/categorize`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function exportBankTransactionsCSV(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    query.set(key, String(value));
  });
  const res = await fetch(`${CONFIG.BASE}/banking/transactions/export?${query.toString()}`, {
    headers: {
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${res.status} ${res.statusText} ${text}`.trim());
  }
  return res.blob();
}

export async function exportMileageCSV(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    query.set(key, String(value));
  });
  const res = await fetch(`${CONFIG.BASE}/bookkeeping/mileage/export?${query.toString()}`, {
    headers: {
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${res.status} ${res.statusText} ${text}`.trim());
  }
  return res.blob();
}

export function adminStartImpersonation(payload) {
  return apiFetch(`/admin/impersonations`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function adminExitImpersonation() {
  return apiFetch(`/admin/impersonations/exit`, {
    method: "POST",
  });
}

// profession profiles
export function fetchProfessionCatalog() {
  return apiFetch(`/profession/catalog`);
}

export function fetchProfessionProfile(options = {}) {
  if (options?.forceRefresh) {
    return apiFetch(`/profession/profile`).then((payload) => {
      writeScopedCache(CACHE_KEYS.professionProfile, payload);
      return payload;
    });
  }
  return cachedGetWithBootstrap(`/profession/profile`, CACHE_KEYS.professionProfile, PROFILE_CACHE_TTL_MS);
}

export function updateProfessionProfile(payload) {
  return apiFetch(`/profession/profile`, {
    method: "PUT",
    body: JSON.stringify(payload),
  }).then((response) => {
    writeScopedCache(CACHE_KEYS.professionProfile, response);
    return response;
  });
}

export function updateAutomationTemplates(payload) {
  return apiFetch(`/profession/automation-templates`, {
    method: "PUT",
    body: JSON.stringify(payload),
  }).then((response) => {
    clearScopedCache(CACHE_KEYS.professionProfile);
    return response;
  });
}

export function getNotificationSettings() {
  return apiFetch(`/notifications/settings`);
}

export function updateNotificationSettings(payload) {
  return apiFetch(`/notifications/settings`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function getReminderMessages() {
  return apiFetch(`/notifications/messages`);
}

export function updateReminderMessage(payload) {
  return apiFetch(`/notifications/messages`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function sendNotificationTest() {
  return apiFetch(`/notifications/test`, {
    method: "POST",
  });
}

export function sendManualNotification(payload) {
  return apiFetch(`/notifications/manual`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function getNotificationHistory(limit = 25, { client_id: clientId } = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (Number.isSafeInteger(clientId) && clientId > 0) {
    params.set("client_id", String(clientId));
  }
  return apiFetch(`/notifications/history?${params.toString()}`);
}

export function getPaymentReminders(limit = 100) {
  return apiFetch(`/notifications/payment-reminders?limit=${limit}`);
}

export function retryPaymentReminder(deliveryId, payload = {}) {
  return apiFetch(`/notifications/payment-reminders/${deliveryId}/retry`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function sendPaymentReminderNow(deliveryId, payload = {}) {
  return apiFetch(`/notifications/payment-reminders/${deliveryId}/send-now`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateReminderDeliveryPreference(payload) {
  return apiFetch(`/notifications/reminder-delivery`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function createPaymentReminderCustomerHubLink(deliveryId) {
  return apiFetch(`/notifications/payment-reminders/${deliveryId}/customer-hub-link`, {
    method: "POST",
  });
}

export function getReminderCredits() {
  return apiFetch(`/notifications/credits`);
}

function creditsQuery(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "" || value === "all") return;
    search.set(key, String(value));
  });
  const query = search.toString();
  return query ? `?${query}` : "";
}

export function getCreditsBalance() {
  return apiFetch(`/credits/balance`, { skipAuthInvalid: true });
}

export function getCreditsOverview({ start, end, group } = {}) {
  return apiFetch(`/credits/overview${creditsQuery({ start, end, group })}`);
}

export function getCreditsHistory({ start, end, category, kind, q, limit = 50, offset = 0 } = {}) {
  return apiFetch(`/credits/history${creditsQuery({ start, end, category, kind, q, limit, offset })}`);
}

export function createCreditsTopUp(amountPence) {
  return apiFetch(`/credits/top-up`, {
    method: "POST",
    body: JSON.stringify({ amount_pence: Number(amountPence) }),
  });
}

export function confirmCreditsTopUp(sessionId) {
  return apiFetch(`/credits/top-up/confirm`, {
    method: "POST",
    body: JSON.stringify({ session_id: String(sessionId || "") }),
  });
}

export function getAutoTopUp() {
  return apiFetch(`/credits/auto-top-up`);
}

export function updateAutoTopUp(payload) {
  return apiFetch(`/credits/auto-top-up`, {
    method: "PUT",
    body: JSON.stringify(payload || {}),
  });
}

export function createReminderTopup(payload) {
  return apiFetch(`/notifications/topup`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function getNotificationSenderId() {
  return apiFetch(`/notifications/sender-id`);
}

export function requestNotificationSenderId(payload) {
  return apiFetch(`/notifications/sender-id`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// appointments
export function getAppointments(start, end, params = {}) {
  const search = new URLSearchParams({ start, end });
  if (params.client_id) search.set("client_id", String(params.client_id));
  if (params.payment_filter && params.payment_filter !== "all") {
    search.set("payment_filter", String(params.payment_filter));
  }
  return apiFetch(`/appointments?${search.toString()}`);
}

export async function getLatestClientAppointmentBefore(clientId, before, options = {}) {
  const search = new URLSearchParams({
    client_id: String(clientId),
    on_or_before: before,
  });
  const cacheKey = `appointments.latestBefore.v1.${clientId}.${before}`;
  return cachedGetWithBootstrap(
    `/appointments/latest-client-appointment?${search.toString()}`,
    cacheKey,
    REFERENCE_CACHE_TTL_MS,
    options,
  );
}

export function createAppointment(payload) {
  return queueableMutation(
    `/appointments`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
    { action: "create-appointment" },
  ).then((response) => {
    clearAppointmentRelatedCaches();
    clearScopedCache(CACHE_KEYS.stockItems);
    emitStockRefresh();
    return response;
  });
}

export function ensureAppointmentInvoice(id) {
  return apiFetch(`/appointments/${id}/invoice`, {
    method: "POST",
  }).then((response) => {
    clearAppointmentRelatedCaches();
    clearInvoiceCaches();
    return response;
  });
}

export function updateAppointment(id, payload) {
  return queueableMutation(
    `/appointments/${id}`,
    {
      method: "PUT",
      body: JSON.stringify(payload),
    },
    { action: "update-appointment", appointmentId: id },
  ).then((response) => {
    clearAppointmentRelatedCaches();
    clearScopedCache(CACHE_KEYS.stockItems);
    emitStockRefresh();
    return response;
  });
}

export function deleteAppointment(id) {
  return queueableMutation(
    `/appointments/${id}`,
    {
      method: "DELETE",
    },
    { action: "delete-appointment", appointmentId: id },
  ).then((response) => {
    clearAppointmentRelatedCaches();
    clearScopedCache(CACHE_KEYS.stockItems);
    emitStockRefresh();
    return response;
  });
}

// suggest
export function suggestSlots(payload) {
  return apiFetch(`/suggest`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function getSuggestAllowance() {
  return apiFetch(`/suggest/allowance`);
}

export function getLiveTravelCostOverview(payload) {
  return apiFetch(`/suggest/live-travel-costs`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}


// dashboard
export function getDashboardSummary() {
  return cachedGetWithBootstrap(`/dashboard/summary`, CACHE_KEYS.dashboardSummary, REFERENCE_CACHE_TTL_MS);
}

export function getDashboardActivity(limit = 5) {
  return cachedGetWithBootstrap(
    `/dashboard/activity?limit=${limit}`,
    `${CACHE_KEYS.dashboardActivity}.${limit}`,
    REFERENCE_CACHE_TTL_MS,
  );
}

export function getUserActivityLog({ limit = 50, offset = 0 } = {}) {
  return apiFetch(`/activity?limit=${limit}&offset=${offset}`);
}

export function sendClientTelemetry(payload, { keepalive = false } = {}) {
  return apiFetch(`/activity/telemetry`, {
    method: "POST",
    body: JSON.stringify(payload || {}),
    keepalive,
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export function getBusinessReport(params = {}) {
  const search = new URLSearchParams();
  if (params.start) search.set("start", params.start);
  if (params.end) search.set("end", params.end);
  if (params.history_months) search.set("history_months", String(params.history_months));
  return apiFetch(`/dashboard/business-report?${search.toString()}`);
}

// clients
export function listClients(options = {}) {
  return cachedGetWithBootstrap(`/clients`, CACHE_KEYS.clients, REFERENCE_CACHE_TTL_MS, options);
}

export function listClientProfileOptions(clientId) {
  return apiFetch(`/clients/${encodeURIComponent(clientId)}/profile-options`);
}

export function createClient(payload) {
  return queueableMutation(
    `/clients`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
    { action: "create-client" },
  ).then((response) => {
    clearClientRelatedCaches();
    return response;
  });
}

export function createClientWithLocation(payload) {
  return queueableMutation(
    `/clients/with-location`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
    { action: "create-client-with-location" },
  ).then((response) => {
    clearClientRelatedCaches();
    clearScopedCache(CACHE_KEYS.clientLocations);
    return response;
  });
}

export function createClientWithLocations(payload) {
  return queueableMutation(
    `/clients/with-locations`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
    { action: "create-client-with-locations" },
  ).then((response) => {
    clearClientRelatedCaches();
    clearScopedCache(CACHE_KEYS.clientLocations);
    return response;
  });
}

export function updateClient(clientId, payload) {
  return queueableMutation(
    `/clients/${clientId}`,
    {
      method: "PUT",
      body: JSON.stringify(payload),
    },
    { action: "update-client", clientId },
  ).then((response) => {
    clearClientRelatedCaches();
    if (payload?.primary_location) {
      clearScopedCache(clientLocationsCacheKey(clientId));
      clearScopedCache(CACHE_KEYS.clientLocations);
    }
    return response;
  });
}

export function updateClientReminderChannel(clientId, reminderChannel) {
  return queueableMutation(
    `/clients/${clientId}/reminder-channel`,
    {
      method: "PATCH",
      body: JSON.stringify({ reminder_channel: reminderChannel }),
    },
    { action: "update-client-reminder-channel", clientId },
  ).then((response) => {
    clearClientRelatedCaches();
    return response;
  });
}

export function updateClientReminderRecipient(clientId, reminderRecipient) {
  return queueableMutation(
    `/clients/${clientId}/reminder-recipient`,
    {
      method: "PATCH",
      body: JSON.stringify({ reminder_recipient: reminderRecipient }),
    },
    { action: "update-client-reminder-recipient", clientId },
  ).then((response) => {
    clearClientRelatedCaches();
    return response;
  });
}

export async function uploadClientPhoto(clientId, file) {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${CONFIG.BASE}/clients/${clientId}/photo`, {
    method: "POST",
    headers: {
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    body: form,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${res.status} ${res.statusText} ${text}`.trim());
  }
  clearClientRelatedCaches();
  const text = await res.text().catch(() => "");
  return text ? JSON.parse(text) : null;
}

export function deleteClientRecord(clientId) {
  return queueableMutation(
    `/clients/${clientId}`,
    {
      method: "DELETE",
    },
    { action: "delete-client", clientId },
  ).then((response) => {
    clearClientRelatedCaches();
    return response;
  });
}

export function importClients(payload) {
  return queueableMutation(
    `/clients/import`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
    { action: "import-clients" },
  ).then((response) => {
    clearScopedCache(CACHE_KEYS.clients);
    return response;
  });
}

// client locations
export function listClientLocations(clientId, options = {}) {
  const qs = clientId ? `?client_id=${clientId}` : "";
  const cacheKey = clientLocationsCacheKey(clientId);
  return cachedGetWithBootstrap(`/client-locations${qs}`, cacheKey, REFERENCE_CACHE_TTL_MS, options);
}

export function createClientLocation(payload) {
  return queueableMutation(
    `/client-locations`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
    { action: "create-location", clientId: payload?.client_id },
  ).then((response) => {
    clearScopedCache(clientLocationsCacheKey(payload?.client_id));
    return response;
  });
}

export function updateClientLocation(locationId, payload) {
  return queueableMutation(
    `/client-locations/${locationId}`,
    {
      method: "PUT",
      body: JSON.stringify(payload),
    },
    { action: "update-location", locationId },
  ).then((response) => {
    clearScopedCache(clientLocationsCacheKey(payload?.client_id));
    clearScopedCache(CACHE_KEYS.clientLocations);
    return response;
  });
}

export function deleteClientLocation(locationId) {
  return queueableMutation(
    `/client-locations/${locationId}`,
    {
      method: "DELETE",
    },
    { action: "delete-location", locationId },
  ).then((response) => {
    clearScopedCache(CACHE_KEYS.clientLocations);
    return response;
  });
}

// services
export function listServices(options = {}) {
  return cachedGetWithBootstrap(`/services`, CACHE_KEYS.services, REFERENCE_CACHE_TTL_MS, options);
}

export function createService(payload) {
  return queueableMutation(
    `/services`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
    { action: "create-service" },
  ).then((response) => {
    clearScopedCache(CACHE_KEYS.services);
    clearScopedCache(CACHE_KEYS.stockItems);
    emitStockRefresh();
    return response;
  });
}

export function updateService(serviceId, payload) {
  return queueableMutation(
    `/services/${serviceId}`,
    {
      method: "PUT",
      body: JSON.stringify(payload),
    },
    { action: "update-service", serviceId },
  ).then((response) => {
    clearScopedCache(CACHE_KEYS.services);
    clearScopedCache(CACHE_KEYS.stockItems);
    emitStockRefresh();
    return response;
  });
}

export function deleteServiceRecord(serviceId) {
  return queueableMutation(
    `/services/${serviceId}`,
    {
      method: "DELETE",
    },
    { action: "delete-service", serviceId },
  ).then((response) => {
    clearScopedCache(CACHE_KEYS.services);
    clearScopedCache(CACHE_KEYS.stockItems);
    emitStockRefresh();
    return response;
  });
}

// stock
export function listStockItems() {
  return cachedGetWithBootstrap(`/stock-items`, CACHE_KEYS.stockItems, REFERENCE_CACHE_TTL_MS);
}

export function extractStockItemMetadata(payload) {
  return apiFetch(`/stock-items/extract`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function uploadStockImage(file) {
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(`${CONFIG.BASE}/stock-items/image-upload`, {
    method: "POST",
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
    body: formData,
  });
  if (!res.ok) {
    await throwApiError(res);
  }
  return res.json();
}

export function createStockItem(payload) {
  return queueableMutation(
    `/stock-items`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
    { action: "create-stock-item" },
  ).then((response) => {
    clearScopedCache(CACHE_KEYS.stockItems);
    clearScopedCache(CACHE_KEYS.services);
    emitStockRefresh();
    return response;
  });
}

export function updateStockItem(stockItemId, payload) {
  return queueableMutation(
    `/stock-items/${stockItemId}`,
    {
      method: "PUT",
      body: JSON.stringify(payload),
    },
    { action: "update-stock-item", stockItemId },
  ).then((response) => {
    clearScopedCache(CACHE_KEYS.stockItems);
    clearScopedCache(CACHE_KEYS.services);
    emitStockRefresh();
    return response;
  });
}

export function deleteStockItem(stockItemId) {
  return queueableMutation(
    `/stock-items/${stockItemId}`,
    {
      method: "DELETE",
    },
    { action: "delete-stock-item", stockItemId },
  ).then((response) => {
    clearScopedCache(CACHE_KEYS.stockItems);
    clearScopedCache(CACHE_KEYS.services);
    emitStockRefresh();
    return response;
  });
}

export function cleanStockItem(stockItemId, payload = {}) {
  return queueableMutation(
    `/stock-items/${stockItemId}/clean`,
    {
      method: "POST",
      body: JSON.stringify(payload || {}),
    },
    { action: "clean-stock-item", stockItemId },
  ).then((response) => {
    clearScopedCache(CACHE_KEYS.stockItems);
    emitStockRefresh();
    return response;
  });
}

export function useStockItem(stockItemId, payload) {
  return queueableMutation(
    `/stock-items/${stockItemId}/use`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
    { action: "use-stock-item", stockItemId },
  ).then((response) => {
    clearScopedCache(CACHE_KEYS.stockItems);
    emitStockRefresh();
    return response;
  });
}

export function adjustStockItem(stockItemId, payload) {
  return queueableMutation(
    `/stock-items/${stockItemId}/adjust`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
    { action: "adjust-stock-item", stockItemId },
  ).then((response) => {
    clearScopedCache(CACHE_KEYS.stockItems);
    emitStockRefresh();
    return response;
  });
}

// booking
export function getBookingConfig(options = {}) {
  return cachedGetWithBootstrap(`/booking/config`, CACHE_KEYS.bookingConfig, REFERENCE_CACHE_TTL_MS, options);
}

export function updateBookingConfig(payload) {
  return queueableMutation(
    `/booking/config`,
    {
      method: "PUT",
      body: JSON.stringify(payload),
    },
    { action: "update-booking-config" },
  ).then((response) => {
    if (response?.queued) {
      clearScopedCache(CACHE_KEYS.bookingConfig);
    } else if (response) {
      writeScopedCache(CACHE_KEYS.bookingConfig, response);
    } else {
      clearScopedCache(CACHE_KEYS.bookingConfig);
    }
    return response;
  });
}


// AI Receptionist
export function getAiReceptionistConfig() {
  return apiFetch(`/ai-receptionist/config`);
}

export function autofillAiReceptionistKnowledge() {
  return apiFetch(`/ai-receptionist/knowledge/autofill`, { method: "POST" });
}

export function updateAiReceptionistConfig(payload) {
  return apiFetch(`/ai-receptionist/config`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function previewAiReceptionistVoice(payload) {
  return apiBlobFetch(`/ai-receptionist/voice-preview`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function activateAiReceptionist() {
  return apiFetch(`/ai-receptionist/activate`, { method: "POST" });
}

export function pauseAiReceptionist() {
  return apiFetch(`/ai-receptionist/pause`, { method: "POST" });
}

export function listAiReceptionistCalls() {
  return apiFetch(`/ai-receptionist/calls`);
}

export function getAiReceptionistTestCall() {
  return apiFetch(`/ai-receptionist/test-call`);
}

export function startAiReceptionistTestCall() {
  return apiFetch(`/ai-receptionist/test-call/start`, { method: "POST" });
}

export function getAiReceptionistCall(callId) {
  return apiFetch(`/ai-receptionist/calls/${callId}`);
}

export function updateAiReceptionistCall(callId, payload) {
  return apiFetch(`/ai-receptionist/calls/${callId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function getAiReceptionistRecording(callId) {
  return apiBlobFetch(`/ai-receptionist/calls/${callId}/recording`);
}

export function deleteAiReceptionistRecording(callId) {
  return apiFetch(`/ai-receptionist/calls/${callId}/recording`, { method: "DELETE" });
}

export function exportAiReceptionistCalls() {
  return apiBlobFetch(`/ai-receptionist/calls-export`);
}

export function deleteAiReceptionistCall(callId) {
  return apiFetch(`/ai-receptionist/calls/${callId}`, { method: "DELETE" });
}

export function listAiReceptionistCustomers() {
  return apiFetch(`/ai-receptionist/customers`);
}

export function updateAiReceptionistCustomer(customerId, payload) {
  return apiFetch(`/ai-receptionist/customers/${customerId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function listAiReceptionistBookings() {
  return apiFetch(`/ai-receptionist/bookings`);
}

export function listAiReceptionistConversations() {
  return apiFetch(`/ai-receptionist/conversations`);
}

export function getAiReceptionistConversation(conversationId) {
  return apiFetch(`/ai-receptionist/conversations/${conversationId}`);
}

export function sendAiReceptionistConversationMessage(conversationId, body) {
  return apiFetch(`/ai-receptionist/conversations/${conversationId}/messages`, {
    method: "POST",
    body: JSON.stringify({ body }),
  });
}

export function generateAiReceptionistConversationDraft(conversationId) {
  return apiFetch(`/ai-receptionist/conversations/${conversationId}/draft`, { method: "POST" });
}

export function getAiReceptionistWhatsAppConnection() {
  return apiFetch(`/ai-receptionist/whatsapp/connection`);
}

export function connectAiReceptionistWhatsApp(payload) {
  return apiFetch(`/ai-receptionist/whatsapp/connection`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function disconnectAiReceptionistWhatsApp() {
  return apiFetch(`/ai-receptionist/whatsapp/connection`, { method: "DELETE" });
}

export function listAiReceptionistWhatsAppConversations() {
  return apiFetch(`/ai-receptionist/whatsapp/conversations`);
}

export function getAiReceptionistWhatsAppConversation(conversationId) {
  return apiFetch(`/ai-receptionist/whatsapp/conversations/${conversationId}`);
}

export function generateAiReceptionistWhatsAppDraft(messageId) {
  return apiFetch(`/ai-receptionist/whatsapp/messages/${messageId}/draft`, { method: "POST" });
}

export function sendAiReceptionistWhatsAppDraft(messageId, payload) {
  return apiFetch(`/ai-receptionist/whatsapp/messages/${messageId}/send`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function getAiReceptionistEmailConnection() {
  return apiFetch(`/ai-receptionist/email/connection`);
}

export function startAiReceptionistEmailConnection(provider) {
  return apiFetch(`/ai-receptionist/email/oauth/start/${provider}`, { method: "POST" });
}

export function disconnectAiReceptionistEmailConnection() {
  return apiFetch(`/ai-receptionist/email/connection`, { method: "DELETE" });
}

export function updateAiReceptionistEmailSettings(payload) {
  return apiFetch(`/ai-receptionist/email/connection`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function syncAiReceptionistEmail() {
  return apiFetch(`/ai-receptionist/email/sync`, { method: "POST" });
}

export function listAiReceptionistEmailThreads() {
  return apiFetch(`/ai-receptionist/email/threads`);
}

export function getAiReceptionistEmailThread(threadId) {
  return apiFetch(`/ai-receptionist/email/threads/${threadId}`);
}

export function updateAiReceptionistEmailDraft(threadId, payload) {
  return apiFetch(`/ai-receptionist/email/threads/${threadId}/draft`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function regenerateAiReceptionistEmailDraft(threadId, payload) {
  return apiFetch(`/ai-receptionist/email/threads/${threadId}/draft/generate`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function getAiReceptionistIntegration() {
  return apiFetch(`/ai-receptionist/integration`);
}

export function getAiReceptionistIntegrationRequest() {
  return apiFetch(`/ai-receptionist/integration-request`);
}

export function submitAiReceptionistIntegrationRequest(payload) {
  return apiFetch(`/ai-receptionist/integration-request`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateAiReceptionistIntegration(payload) {
  return apiFetch(`/ai-receptionist/integration`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function rotateAiReceptionistIntegrationSecret() {
  return apiFetch(`/ai-receptionist/integration/rotate-secret`, { method: "POST" });
}

export function testAiReceptionistIntegration() {
  return apiFetch(`/ai-receptionist/integration/test`, { method: "POST" });
}

export function submitAiReceptionistNumberCompliance(payload) {
  return apiFetch(`/ai-receptionist/number/compliance`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function refreshAiReceptionistNumberCompliance() {
  return apiFetch(`/ai-receptionist/number/compliance/refresh`, { method: "POST" });
}

export function createAiReceptionistNumberCheckout(payload) {
  return apiFetch(`/ai-receptionist/number/checkout`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function retryAiReceptionistNumberProvisioning(payload = {}) {
  return apiFetch(`/ai-receptionist/number/provision`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function releaseAiReceptionistNumber() {
  return apiFetch(`/ai-receptionist/number/release`, {
    method: "POST",
    body: JSON.stringify({ confirm: true }),
  });
}


export function getBookingRecentContacts(options = {}) {
  return cachedGetWithBootstrap(
    `/booking/recent-contacts`,
    CACHE_KEYS.bookingRecentContacts,
    REFERENCE_CACHE_TTL_MS,
    options,
  );
}

export function getBookingRecentAppointments(options = {}) {
  return cachedGetWithBootstrap(
    `/booking/recent-appointments`,
    CACHE_KEYS.bookingRecentAppointments,
    REFERENCE_CACHE_TTL_MS,
    options,
  );
}

export function getBookingRecentEnquiries(options = {}) {
  return cachedGetWithBootstrap(
    `/booking/recent-enquiries`,
    CACHE_KEYS.bookingRecentEnquiries,
    REFERENCE_CACHE_TTL_MS,
    options,
  );
}

export function fetchBookingPublic(slug, params = {}) {
  const search = new URLSearchParams(params);
  const qs = search.toString();
  return apiFetch(`/booking/public/${slug}${qs ? `?${qs}` : ""}`);
}

export function createBookingRequest(slug, payload) {
  return apiFetch(`/booking/public/${slug}/request`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function createBookingOutOfAreaLead(slug, payload) {
  return apiFetch(`/booking/public/${slug}/out-of-area-lead`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function createBookingEnquiry(slug, payload) {
  return apiFetch(`/booking/public/${slug}/enquiries`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function verifyBookingEnquiryInstall(payload) {
  return apiFetch(`/booking/enquiries/verify`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function trackBookingEvent(slug, payload) {
  return apiFetch(`/booking/events/${slug}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function getBookingInsights(daysOrOptions = 7) {
  let days = 7;
  const params = new URLSearchParams();
  if (typeof daysOrOptions === "number") {
    days = daysOrOptions;
  } else if (daysOrOptions && typeof daysOrOptions === "object") {
    days = Number.isFinite(daysOrOptions.days) ? daysOrOptions.days : 7;
    if (daysOrOptions.sinceUpdate) params.set("since_update", "1");
    if (daysOrOptions.serviceId) params.set("service_id", String(daysOrOptions.serviceId));
    if (daysOrOptions.entryPath) params.set("entry_path", daysOrOptions.entryPath);
    if (daysOrOptions.availabilityState) params.set("availability_state", daysOrOptions.availabilityState);
    if (daysOrOptions.timeOfDay) params.set("time_of_day", daysOrOptions.timeOfDay);
    if (daysOrOptions.dayOfWeek) params.set("day_of_week", daysOrOptions.dayOfWeek);
  }
  params.set("days", String(days));
  return apiFetch(`/booking/insights?${params.toString()}`);
}

export function getBookingRawEvents(options = {}) {
  const params = new URLSearchParams();
  const days = Number.isFinite(options.days) ? options.days : 7;
  params.set("days", String(days));
  if (options.sinceUpdate) params.set("since_update", "1");
  if (options.serviceId) params.set("service_id", String(options.serviceId));
  if (options.entryPath) params.set("entry_path", options.entryPath);
  if (options.availabilityState) params.set("availability_state", options.availabilityState);
  if (options.timeOfDay) params.set("time_of_day", options.timeOfDay);
  if (options.dayOfWeek) params.set("day_of_week", options.dayOfWeek);
  return apiFetch(`/booking/raw-events?${params.toString()}`);
}

export function createBookingHold(slug, payload) {
  return apiFetch(`/booking/public/${slug}/hold`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

function withBookingManageParams(path, paramsOrCode = {}) {
  const params = new URLSearchParams();
  if (typeof paramsOrCode === "string") {
    if (paramsOrCode) params.set("code", paramsOrCode);
  } else if (paramsOrCode && typeof paramsOrCode === "object") {
    Object.entries(paramsOrCode).forEach(([key, value]) => {
      if (value == null || value === "") return;
      params.set(key, String(value));
    });
  }
  const qs = params.toString();
  return `${path}${qs ? `?${qs}` : ""}`;
}

export function fetchBookingManage(slug, token, params = {}) {
  return apiFetch(withBookingManageParams(`/booking/public/${slug}/manage/${token}`, params));
}

export function recordBookingPaymentCancelled(slug, token, params = {}) {
  return apiFetch(withBookingManageParams(`/booking/public/${slug}/manage/${token}/payment-cancelled`, params), {
    method: "POST",
  });
}

export function createBookingCheckout(slug, token, params = {}) {
  return apiFetch(withBookingManageParams(`/booking/public/${slug}/manage/${token}/checkout`, params), {
    method: "POST",
  });
}

export async function downloadBookingReceipt(slug, token, params = {}) {
  const url = `${CONFIG.BASE}${withBookingManageParams(`/booking/public/${slug}/manage/${token}/receipt`, params)}`;
  const res = await fetch(url, {
    headers: {
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || "Unable to download receipt");
  }
  return res.blob();
}

export function cancelBooking(slug, token, payload = {}, params = {}) {
  return apiFetch(withBookingManageParams(`/booking/public/${slug}/manage/${token}/cancel`, params), {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function rescheduleBooking(slug, token, payload, params = {}) {
  return apiFetch(withBookingManageParams(`/booking/public/${slug}/manage/${token}/reschedule`, params), {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function uploadBookingLogo(file) {
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(`${CONFIG.BASE}/booking/logo-upload`, {
    method: "POST",
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
    body: formData,
  });
  if (!res.ok) {
    await throwApiError(res);
  }
  return res.json();
}

export async function uploadBookingCover(file) {
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(`${CONFIG.BASE}/booking/cover-upload`, {
    method: "POST",
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
    body: formData,
  });
  if (!res.ok) {
    await throwApiError(res);
  }
  return res.json();
}

// payment settings
export function getCachedPaymentSettingsSnapshot() {
  const data = readScopedCacheData(CACHE_KEYS.paymentSettings);
  return data && typeof data === "object" ? data : null;
}

export function getPaymentSettings(options = {}) {
  return cachedGetWithBootstrap(
    `/payments/settings`,
    CACHE_KEYS.paymentSettings,
    REFERENCE_CACHE_TTL_MS,
    {
      preferFresh: options?.forceRefresh === true || options?.preferCache !== true,
      onRevalidate: options?.onRevalidate,
    },
  );
}

export function getAppointmentPaymentDefault() {
  return apiFetch(`/payments/appointment-default`);
}

export function updatePaymentSettings(payload) {
  return apiFetch(`/payments/settings`, {
    method: "PUT",
    body: JSON.stringify(payload),
  }).then((response) => {
    writeScopedCache(CACHE_KEYS.paymentSettings, response);
    return response;
  });
}

export function updateAppointmentPaymentDefault(payload) {
  return apiFetch(`/payments/appointment-default`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function createStripeOnboarding() {
  return apiFetch(`/payments/stripe/onboarding`, {
    method: "POST",
  });
}

export function createStripeLoginLink() {
  return apiFetch(`/payments/stripe/login-link`, {
    method: "POST",
  });
}

export function createProCheckoutSession(payload) {
  return apiFetch(`/billing/checkout`, {
    method: "POST",
    body: JSON.stringify(payload),
  }).then((response) => {
    clearScopedCache(CACHE_KEYS.billingStatus);
    return response;
  });
}

export function getBillingStatus(options = {}) {
  return cachedGetWithBootstrap(`/billing/status`, CACHE_KEYS.billingStatus, BILLING_CACHE_TTL_MS, {
    preferFresh: options?.forceRefresh === true || options?.preferFresh !== false,
  });
}

export function createBillingPortalSession() {
  return apiFetch(`/billing/portal`, {
    method: "POST",
  }).then((response) => {
    clearScopedCache(CACHE_KEYS.billingStatus);
    return response;
  });
}

export function cancelProfessionalSubscription() {
  return apiFetch(`/billing/cancel`, {
    method: "POST",
  }).then((response) => {
    clearScopedCache(CACHE_KEYS.billingStatus);
    return response;
  });
}

export function switchToStarterSubscription(payload) {
  return apiFetch(`/billing/switch-to-starter`, {
    method: "POST",
    body: JSON.stringify(payload),
  }).then((response) => {
    clearScopedCache(CACHE_KEYS.billingStatus);
    return response;
  });
}

export function switchToBasicSubscription(payload) {
  return apiFetch(`/billing/switch-to-basic`, {
    method: "POST",
    body: JSON.stringify(payload),
  }).then((response) => {
    clearScopedCache(CACHE_KEYS.billingStatus);
    return response;
  });
}

// One billing card for the Professional plan and credits (docs/credits/API.md).
export function getBillingPaymentMethod() {
  return apiFetch(`/billing/payment-method`);
}

export function startBillingCardSetup(returnTo = "billing") {
  return apiFetch(`/billing/payment-method/setup`, {
    method: "POST",
    body: JSON.stringify({ return_to: returnTo === "credits" ? "credits" : "billing" }),
  });
}

export function confirmBillingCard(sessionId) {
  return apiFetch(`/billing/payment-method/confirm`, {
    method: "POST",
    body: JSON.stringify({ session_id: String(sessionId || "") }),
  });
}

export function removeBillingCard() {
  return apiFetch(`/billing/payment-method`, { method: "DELETE" });
}

export function createPaymentLink(invoiceId) {
  return apiFetch(`/payments/links/${invoiceId}`, {
    method: "POST",
  });
}

export function createPaymentRequest(invoiceId, payload) {
  return apiFetch(`/payments/requests/${invoiceId}`, {
    method: "POST",
    body: JSON.stringify(payload),
  }).then((response) => {
    clearInvoiceCaches();
    clearPaymentActivityCaches();
    clearDashboardCaches();
    return response;
  });
}

export function getCachedPaymentRequestHistorySnapshot() {
  const data = readScopedCacheData(CACHE_KEYS.paymentRequestHistory);
  return Array.isArray(data) ? data : [];
}

export function listPaymentRequestHistory(options = {}) {
  return cachedGetWithBootstrap(
    "/payments/requests/history",
    CACHE_KEYS.paymentRequestHistory,
    REFERENCE_CACHE_TTL_MS,
    {
      preferFresh: options?.forceRefresh === true,
      onRevalidate: options?.onRevalidate,
    },
  );
}

export function getCachedPaymentOperationsSnapshot() {
  const data = readScopedCacheData(CACHE_KEYS.paymentOperations);
  return Array.isArray(data) ? data : [];
}

export function listPaymentOperations(options = {}) {
  return cachedGetWithBootstrap(
    "/payments/operations",
    CACHE_KEYS.paymentOperations,
    REFERENCE_CACHE_TTL_MS,
    {
      preferFresh: options?.forceRefresh === true,
      onRevalidate: options?.onRevalidate,
    },
  );
}

export function createPaymentRefund(invoiceId, payload) {
  return apiFetch(`/payments/operations/${invoiceId}/refund`, {
    method: "POST",
    body: JSON.stringify(payload),
  }).then((response) => {
    clearInvoiceCaches();
    clearPaymentActivityCaches();
    clearDashboardCaches();
    return response;
  });
}

export function cancelPaymentAppointment(invoiceId, payload) {
  return apiFetch(`/payments/operations/${invoiceId}/cancel`, {
    method: "POST",
    body: JSON.stringify(payload),
  }).then((response) => {
    clearInvoiceCaches();
    clearPaymentActivityCaches();
    clearDashboardCaches();
    return response;
  });
}

// expenses
export function listExpenses() {
  return apiFetch(`/expenses`);
}

export function createExpense(payload) {
  return apiFetch(`/expenses`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateExpense(expenseId, payload) {
  return apiFetch(`/expenses/${expenseId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function deleteExpense(expenseId) {
  return apiFetch(`/expenses/${expenseId}`, {
    method: "DELETE",
  });
}

export function listExpenseBankTransactions() {
  return apiFetch(`/expenses/bank-transactions`);
}

export function createExpenseBankTransaction(payload) {
  return apiFetch(`/expenses/bank-transactions`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// bookkeeping
export function listBookkeepingAccounts() {
  return apiFetch(`/bookkeeping/accounts`);
}

export function getBookkeepingSettings() {
  return apiFetch(`/bookkeeping/settings`);
}

export function updateBookkeepingSettings(payload) {
  return apiFetch(`/bookkeeping/settings`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

function normalizeMileageAddressPayload(payload) {
  if (!payload || typeof payload !== "object") return payload;
  const next = { ...payload };
  if ("start_address" in next) {
    next.start_address = toSingleLineAddress(next.start_address);
  }
  if ("end_address" in next) {
    next.end_address = toSingleLineAddress(next.end_address);
  }
  return next;
}

export function fetchMileageRoutes(payload) {
  return apiFetch(`/bookkeeping/mileage/routes`, {
    method: "POST",
    body: JSON.stringify(normalizeMileageAddressPayload(payload)),
  });
}

export function listMileage() {
  return apiFetch(`/bookkeeping/mileage`);
}

export function listMileageDayPlans(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    query.set(key, String(value));
  });
  return apiFetch(`/bookkeeping/mileage/day-plans${query.toString() ? `?${query.toString()}` : ""}`);
}

export function saveMileageDayPlan(date, payload) {
  return apiFetch(`/bookkeeping/mileage/day-plans/${date}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function deleteMileageDayPlan(date, payload = {}) {
  return apiFetch(`/bookkeeping/mileage/day-plans/${date}`, {
    method: "DELETE",
    body: JSON.stringify(payload),
  });
}

export function createMileage(payload) {
  return apiFetch(`/bookkeeping/mileage`, {
    method: "POST",
    body: JSON.stringify(normalizeMileageAddressPayload(payload)),
  });
}

export function saveMileageBatch(payload) {
  return apiFetch(`/bookkeeping/mileage/batch`, {
    method: "POST",
    body: JSON.stringify({
      ...payload,
      entries: (payload?.entries || []).map((entry) => normalizeMileageAddressPayload(entry)),
    }),
  });
}

export function updateMileage(entryId, payload) {
  return apiFetch(`/bookkeeping/mileage/${entryId}`, {
    method: "PUT",
    body: JSON.stringify(normalizeMileageAddressPayload(payload)),
  });
}

export function deleteMileage(entryId, payload = {}) {
  return apiFetch(`/bookkeeping/mileage/${entryId}`, {
    method: "DELETE",
    body: JSON.stringify(payload),
  });
}

export function listMileageVehicles() {
  return apiFetch(`/bookkeeping/mileage/vehicles`);
}

export function getMileageRate(date, vehicleId = null) {
  const query = new URLSearchParams({ date: String(date || "") });
  if (vehicleId) query.set("vehicle_id", String(vehicleId));
  return apiFetch(`/bookkeeping/mileage/rate?${query.toString()}`);
}

export function createMileageVehicle(payload) {
  return apiFetch(`/bookkeeping/mileage/vehicles`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateMileageVehicle(vehicleId, payload) {
  return apiFetch(`/bookkeeping/mileage/vehicles/${vehicleId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function listMileageVehicleRates(vehicleId) {
  return apiFetch(`/bookkeeping/mileage/vehicles/${vehicleId}/rates`);
}

export function createMileageVehicleRate(vehicleId, payload) {
  return apiFetch(`/bookkeeping/mileage/vehicles/${vehicleId}/rates`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function getBookkeepingOverview(params = {}) {
  const query = new URLSearchParams(params).toString();
  return apiFetch(`/bookkeeping/overview${query ? `?${query}` : ""}`);
}

export function listBookkeepingIncome() {
  return apiFetch(`/bookkeeping/income`);
}

export function createBookkeepingIncome(payload) {
  return apiFetch(`/bookkeeping/income`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function listBookkeepingExpenses() {
  return apiFetch(`/bookkeeping/expenses`);
}

export function createBookkeepingExpense(payload) {
  return apiFetch(`/bookkeeping/expenses`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function listExpenseReceipts(transactionId) {
  return apiFetch(`/bookkeeping/expenses/${transactionId}/receipts`);
}

export function listBankTransactionReceipts(transactionId) {
  return apiFetch(`/bookkeeping/bank-transactions/${transactionId}/receipts`);
}

export async function uploadExpenseReceipt(transactionId, file, options = {}) {
  const form = new FormData();
  form.append("file", file);
  if (options.allow_locked_edit) {
    form.append("allow_locked_edit", "true");
  }
  if (options.edit_reason) {
    form.append("edit_reason", options.edit_reason);
  }
  const res = await fetch(`${CONFIG.BASE}/bookkeeping/expenses/${transactionId}/receipts`, {
    method: "POST",
    headers: {
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    body: form,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${res.status} ${res.statusText} ${text}`.trim());
  }
  const text = await res.text().catch(() => "");
  return text ? JSON.parse(text) : null;
}

export async function uploadBankTransactionReceipt(transactionId, file, options = {}) {
  const form = new FormData();
  form.append("file", file);
  if (options.allow_locked_edit) {
    form.append("allow_locked_edit", "true");
  }
  if (options.edit_reason) {
    form.append("edit_reason", options.edit_reason);
  }
  const res = await fetch(`${CONFIG.BASE}/bookkeeping/bank-transactions/${transactionId}/receipts`, {
    method: "POST",
    headers: {
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    body: form,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${res.status} ${res.statusText} ${text}`.trim());
  }
  const text = await res.text().catch(() => "");
  return text ? JSON.parse(text) : null;
}

export async function downloadExpenseReceipt(receiptId) {
  const res = await fetch(`${CONFIG.BASE}/bookkeeping/receipts/${receiptId}/download`, {
    headers: {
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${res.status} ${res.statusText} ${text}`.trim());
  }
  return res.blob();
}

export async function deleteExpenseReceipt(receiptId, options = {}) {
  const query = new URLSearchParams();
  if (options.allow_locked_edit) query.set("allow_locked_edit", "true");
  if (options.edit_reason) query.set("edit_reason", options.edit_reason);
  const res = await fetch(
    `${CONFIG.BASE}/bookkeeping/receipts/${receiptId}?${query.toString()}`,
    {
      method: "DELETE",
      headers: {
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
    },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${res.status} ${res.statusText} ${text}`.trim());
  }
  return null;
}

export async function exportExpenseReceiptsZip(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    query.set(key, String(value));
  });
  const res = await fetch(`${CONFIG.BASE}/bookkeeping/expenses/receipts/export?${query.toString()}`, {
    headers: {
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${res.status} ${res.statusText} ${text}`.trim());
  }
  return res.blob();
}

export function lockBookkeepingExpensesForReport(payload) {
  return apiFetch(`/bookkeeping/expenses/lock`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function listBookkeepingTransfers() {
  return apiFetch(`/bookkeeping/transfers`);
}

export function createBookkeepingTransfer(payload) {
  return apiFetch(`/bookkeeping/transfers`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function createBookkeepingOwner(payload) {
  return apiFetch(`/bookkeeping/owner`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function listBookkeepingOwner() {
  return apiFetch(`/bookkeeping/owner`);
}

export function createBookkeepingOpeningBalance(payload) {
  return apiFetch(`/bookkeeping/opening-balance`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function listBookkeepingOpeningBalances() {
  return apiFetch(`/bookkeeping/opening-balances`);
}

export function getBookkeepingPnl(params) {
  const query = new URLSearchParams(params).toString();
  return apiFetch(`/bookkeeping/reports/pnl?${query}`);
}

export function getBookkeepingBalances() {
  return apiFetch(`/bookkeeping/reports/balances`);
}

export function getBookkeepingDashboard() {
  return apiFetch(`/bookkeeping/dashboard`);
}

export function getBookkeepingMonitoring() {
  return apiFetch(`/bookkeeping/monitoring`);
}

export function listBookkeepingAudit(transactionId) {
  const query = transactionId ? `?transaction_id=${transactionId}` : "";
  return apiFetch(`/bookkeeping/audit${query}`);
}

// timeline / appointment lifecycle
export function updateAppointmentStatus(appointmentId, payload) {
  return queueableMutation(
    `/appointments/${appointmentId}/status`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
    {
      action: "update-status",
      appointmentId,
      timelinePatch: {
        appointment_id: appointmentId,
        reason: "status-update",
        changes: {
          status: payload.status,
          status_label: payload.status?.replace(/_/g, " "),
        },
      },
    },
  ).then((response) => {
    clearAppointmentRelatedCaches();
    clearScopedCache(CACHE_KEYS.stockItems);
    emitStockRefresh();
    return response;
  });
}

// timeline
export function getTimelineToday(date = null) {
  const search = new URLSearchParams();
  if (date) search.set("date", date);
  const query = search.toString();
  const path = query ? `/timeline/today?${query}` : `/timeline/today`;
  return cachedGet(path, timelineCacheKey(date)).then((payload) =>
    applyTimelinePatches(payload),
  );
}

export function markRunningLate(appointmentId, payload) {
  return queueableMutation(
    `/timeline/${appointmentId}/running-late`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
    {
      action: "running-late",
      appointmentId,
      timelinePatch: {
        appointment_id: appointmentId,
        reason: "running-late",
        changes: { __pending_running_late: true },
      },
    },
  ).then((response) => {
    clearScopedCacheFamily(CACHE_KEYS.timelineToday);
    return response;
  });
}

// invoices / payments
export function listInvoices(params = {}) {
  const search = new URLSearchParams();
  if (params.status) search.set("status", params.status);
  if (params.date_from) search.set("date_from", params.date_from);
  if (params.date_to) search.set("date_to", params.date_to);
  if (params.search) search.set("search", params.search);
  const qs = search.toString();
  const path = `/invoices${qs ? `?${qs}` : ""}`;
  return cachedGetWithBootstrap(
    path,
    invoiceListCacheKey(params),
    REFERENCE_CACHE_TTL_MS,
    { preferFresh: params.forceRefresh === true },
  );
}

export function markInvoicePaid(invoiceId, payload) {
  return apiFetch(
    `/invoices/${invoiceId}/mark-paid`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  ).then((response) => {
    clearInvoiceCaches();
    clearPaymentActivityCaches();
    clearDashboardCaches();
    return response;
  });
}

export function unmarkInvoicePaid(invoiceId) {
  return apiFetch(
    `/invoices/${invoiceId}/unmark-paid`,
    {
      method: "POST",
      body: JSON.stringify({}),
    },
  ).then((response) => {
    clearInvoiceCaches();
    clearDashboardCaches();
    return response;
  });
}

export function reverseInvoiceManualPayment(invoiceId, manualPaymentId, payload) {
  return apiFetch(
    `/invoices/${invoiceId}/manual-payments/${manualPaymentId}/reverse`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  ).then((response) => {
    clearInvoiceCaches();
    clearDashboardCaches();
    return response;
  });
}

export function linkInvoiceBankTransaction(invoiceId, bankTransactionId) {
  return queueableMutation(
    `/invoices/${invoiceId}/link-bank-transaction`,
    {
      method: "POST",
      body: JSON.stringify({ bank_transaction_id: bankTransactionId }),
    },
    { action: "invoice.link_payment", invoiceId, bank_transaction_id: bankTransactionId },
  ).then((response) => {
    clearInvoiceCaches();
    return response;
  });
}

export function voidInvoice(invoiceId, reason) {
  return queueableMutation(
    `/invoices/${invoiceId}/void`,
    {
      method: "POST",
      body: JSON.stringify({ reason }),
    },
    { action: "invoice.void", invoiceId },
  ).then((response) => {
    clearInvoiceCaches();
    clearDashboardCaches();
    return response;
  });
}

export function unvoidInvoice(invoiceId, reason) {
  return queueableMutation(
    `/invoices/${invoiceId}/unvoid`,
    {
      method: "POST",
      body: JSON.stringify({ reason }),
    },
    { action: "invoice.unvoid", invoiceId },
  ).then((response) => {
    clearInvoiceCaches();
    clearDashboardCaches();
    return response;
  });
}

export function listInvoiceAudit(invoiceId) {
  return apiFetch(`/invoices/${invoiceId}/audit`);
}

export function listInvoiceSendHistory() {
  return apiFetch("/invoices/send-history");
}

export function sendInvoiceEmail(invoiceId, payload) {
  return queueableMutation(
    `/invoices/${invoiceId}/email`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
    { action: "invoice.email", invoiceId },
  );
}

export function revokeInvoiceEmailAccess(invoiceId, payload = {}) {
  return queueableMutation(
    `/invoices/${invoiceId}/email/revoke`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
    { action: "invoice.email.revoke", invoiceId },
  );
}

export function updateInvoice(invoiceId, payload) {
  return queueableMutation(
    `/invoices/${invoiceId}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    },
    { action: "invoice.update", invoiceId },
  ).then((response) => {
    clearInvoiceCaches();
    clearDashboardCaches();
    return response;
  });
}

export async function exportInvoicesCsv(status = "all") {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  const res = await fetch(`${CONFIG.BASE}/invoices/export${qs}`, {
    headers: {
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Export failed: ${text || res.statusText}`);
  }
  return res.blob();
}

export async function exportInvoicesPdfZip(params = {}) {
  const search = new URLSearchParams();
  if (params.status) search.set("status", params.status);
  if (params.date_from) search.set("date_from", params.date_from);
  if (params.date_to) search.set("date_to", params.date_to);
  if (params.search) search.set("search", params.search);
  const qs = search.toString();
  return apiBlobFetch(`/invoices/export/pdfs${qs ? `?${qs}` : ""}`);
}

export async function getInvoicePdf(invoiceId) {
  try {
    return await apiBlobFetch(`/invoices/${encodeURIComponent(invoiceId)}/pdf`);
  } catch (error) {
    if (error?.status !== 404) throw error;
  }

  const invoiceNumber = `INV-${String(invoiceId || 0).padStart(6, "0")}`;
  const zipBlob = await exportInvoicesPdfZip({ status: "all", search: invoiceNumber });
  const { unzipSync } = await import("fflate");
  const zipBuffer = typeof zipBlob.arrayBuffer === "function"
    ? await zipBlob.arrayBuffer()
    : await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error || new Error("Unable to read the invoice export."));
      reader.readAsArrayBuffer(zipBlob);
    });
  const files = unzipSync(new Uint8Array(zipBuffer));
  const pdfBytes = files[`${invoiceNumber}.pdf`];
  if (!pdfBytes) {
    throw new Error("The invoice PDF was not included in the export.");
  }
  return new Blob([pdfBytes], { type: "application/pdf" });
}

// documents
export function listDocuments(params = {}) {
  const search = new URLSearchParams();
  if (params.client_id) search.set("client_id", String(params.client_id));
  const qs = search.toString();
  if (qs) {
    return apiFetch(`/documents?${qs}`);
  }
  return cachedGet(`/documents`, CACHE_KEYS.documents);
}

export function listDocumentTemplates() {
  return apiFetch("/documents/templates");
}

export function createDocumentTemplate(payload) {
  return apiFetch("/documents/templates", {
    method: "POST",
    body: JSON.stringify(payload || {}),
  });
}

export function updateDocumentTemplate(templateId, payload) {
  return apiFetch(`/documents/templates/${templateId}`, {
    method: "PATCH",
    body: JSON.stringify(payload || {}),
  });
}

export function deleteDocumentTemplate(templateId) {
  return apiFetch(`/documents/templates/${templateId}`, {
    method: "DELETE",
  });
}

export function listAppointmentDocumentRuns(params = {}) {
  const search = new URLSearchParams();
  if (params.client_id) search.set("client_id", String(params.client_id));
  if (params.appointment_id) search.set("appointment_id", String(params.appointment_id));
  const qs = search.toString();
  return apiFetch(qs ? `/documents/runs?${qs}` : "/documents/runs");
}

export function createAppointmentDocumentRun(payload) {
  return apiFetch("/documents/runs", {
    method: "POST",
    body: JSON.stringify(payload || {}),
  });
}

export function updateAppointmentDocumentRun(runId, payload) {
  return apiFetch(`/documents/runs/${runId}`, {
    method: "PATCH",
    body: JSON.stringify(payload || {}),
  });
}

export function deleteAppointmentDocumentRun(runId) {
  return apiFetch(`/documents/runs/${runId}`, {
    method: "DELETE",
  });
}

function customerHubFormTokenQuery() {
  const token = readCustomerHubToken();
  return token ? `?hub_token=${encodeURIComponent(token)}` : "";
}

export function sendAppointmentForm(runId, payload = {}) {
  return apiFetch(`/documents/runs/${runId}/send`, {
    method: "POST",
    body: JSON.stringify(payload || {}),
    networkMessage: "Unable to send this form link right now. Check your connection and try again.",
  }).then((response) => {
    clearClientRelatedCaches();
    return response;
  });
}

export function listAppointmentFormSendHistory() {
  return apiFetch("/documents/runs/send-history");
}

export function revokeAppointmentFormAccess(runId, payload = {}) {
  return apiFetch(`/documents/runs/${runId}/revoke`, {
    method: "POST",
    body: JSON.stringify(payload),
  }).then((response) => {
    clearClientRelatedCaches();
    return response;
  });
}

export async function downloadAppointmentFormPdf(runId, title = "appointment-form") {
  const blob = await apiBlobFetch(`/documents/runs/${runId}/pdf`);
  if (typeof window === "undefined") return blob;
  const href = window.URL.createObjectURL(blob);
  const link = window.document.createElement("a");
  link.href = href;
  link.download = `${safeDownloadFilename(title, "appointment-form")}.pdf`;
  window.document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(href);
  return blob;
}

export function getPublicAppointmentForm(token) {
  return apiFetch(`/documents/public-forms/${encodeURIComponent(token)}${customerHubFormTokenQuery()}`, {
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export function startPublicAppointmentForm(token) {
  return apiFetch(`/documents/public-forms/${encodeURIComponent(token)}/start${customerHubFormTokenQuery()}`, {
    method: "POST",
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export function submitPublicAppointmentForm(token, payload = {}) {
  return apiFetch(`/documents/public-forms/${encodeURIComponent(token)}/submit${customerHubFormTokenQuery()}`, {
    method: "POST",
    body: JSON.stringify(payload || {}),
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

// Typed-address links texted by the AI receptionist (no sign-in).
export function getPublicAddressLink(token) {
  return apiFetch(`/public/address-links/${encodeURIComponent(token)}`, {
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export function submitPublicAddressLink(token, payload = {}) {
  return apiFetch(`/public/address-links/${encodeURIComponent(token)}`, {
    method: "POST",
    body: JSON.stringify(payload || {}),
    skipAuthInvalid: true,
    skipAuthRefresh: true,
  });
}

export async function uploadDocumentFile({
  file,
  title,
  doc_type = "general",
  notes,
  document_date,
  expiry_date,
  reminder_lead_days = 30,
  client_id,
  appointment_id,
}) {
  if (!file) throw new Error("Select a file to upload");
  if (!title?.trim()) throw new Error("Document title is required");

  const params = new URLSearchParams();
  params.set("title", title.trim());
  params.set("doc_type", doc_type || "general");
  if (document_date) params.set("document_date", document_date);
  if (expiry_date) params.set("expiry_date", expiry_date);
  if (reminder_lead_days != null) params.set("reminder_lead_days", String(reminder_lead_days));
  if (client_id) params.set("client_id", String(client_id));
  if (appointment_id) params.set("appointment_id", String(appointment_id));
  if (notes) params.set("notes", notes);
  params.set("filename", file.name || `${title.trim()}.bin`);
  if (file.type) params.set("mime_type", file.type);

  const res = await fetch(`${CONFIG.BASE}/documents?${params.toString()}`, {
    method: "POST",
    headers: {
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      "Content-Type": file.type || "application/octet-stream",
    },
    body: file,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Upload failed (${res.status})`);
  }
  const payload = await res.json();
  clearScopedCache(CACHE_KEYS.documents);
  return payload;
}

export function updateDocumentMetadata(documentId, payload) {
  return apiFetch(`/documents/${documentId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  }).then((response) => {
    clearScopedCache(CACHE_KEYS.documents);
    return response;
  });
}

export function deleteDocumentRecord(documentId) {
  return apiFetch(`/documents/${documentId}`, {
    method: "DELETE",
  }).then((response) => {
    clearScopedCache(CACHE_KEYS.documents);
    return response;
  });
}

export function sendDocumentToCustomerHub(documentId, payload = {}) {
  return apiFetch(`/documents/${documentId}/send`, {
    method: "POST",
    body: JSON.stringify(payload || {}),
    networkMessage: "Unable to send this document to Customer Hub right now. Check your connection and try again.",
  }).then((response) => {
    clearScopedCache(CACHE_KEYS.documents);
    return response;
  });
}

export async function downloadDocument(documentId) {
  const res = await fetch(`${CONFIG.BASE}/documents/${documentId}/download`, {
    headers: {
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || "Download failed");
  }
  return res.blob();
}

export function runDocumentReminderSweep() {
  return apiFetch(`/documents/run-reminders`, {
    method: "POST",
  });
}

export function sendDocumentReminder(documentId) {
  return apiFetch(`/documents/${documentId}/remind`, {
    method: "POST",
  });
}
